// src/pages/Students.js
import React, { useState, useEffect, useMemo } from "react";
import api from "../api";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import "./Students.css";

// permission helpers
const readStoredArray = (key) => {
  try {
    const localValue = JSON.parse(localStorage.getItem(key) || "[]");
    if (Array.isArray(localValue) && localValue.length) return localValue;
  } catch {}

  try {
    const sessionValue = JSON.parse(sessionStorage.getItem(key) || "[]");
    if (Array.isArray(sessionValue) && sessionValue.length) return sessionValue;
  } catch {}

  return [];
};

const getPermissionFlags = () => {
  const singleRole = localStorage.getItem("userRole") || sessionStorage.getItem("userRole");
  const multiRoles = readStoredArray("roles");
  const storedPermissions = readStoredArray("permissions");

  const roles = (multiRoles.length ? multiRoles : [singleRole].filter(Boolean))
    .map((role) => String(role || "").trim().toLowerCase())
    .filter(Boolean);

  const permissions = storedPermissions
    .map((permission) => String(permission || "").trim().toLowerCase())
    .filter(Boolean);

  const isAdmin = roles.includes("admin");
  const isSuperadmin = roles.includes("superadmin");
  const isPrincipal = roles.includes("principal");
  const isAccounts = roles.includes("accounts");
  const isFrontoffice = roles.includes("frontoffice");
  const isCoordinator = roles.includes("academic_coordinator") || roles.includes("coordinator");

  // Safe fallback while old logins / old role-based flows still exist.
  const fallbackManage = isAdmin || isSuperadmin || isAccounts || isFrontoffice;
  const fallbackDelete = isSuperadmin;

  const hasPermission = (slug, fallback = false) =>
    isSuperadmin ||
    (permissions.length
      ? permissions.includes(String(slug || "").toLowerCase())
      : fallback);

  const canViewStudents = hasPermission("students_view", true);
  const canAddStudents = hasPermission("students_add", fallbackManage);
  const canEditStudents = hasPermission("students_edit", fallbackManage);
  const canDeleteStudents = hasPermission("students_delete", fallbackDelete);
  const canToggleStudentStatus = hasPermission("students_status_toggle", fallbackManage);
  const canUploadStudentPhoto = hasPermission("students_photo_upload", fallbackManage);
  const canExportStudents = hasPermission("students_export", fallbackManage);
  const canImportStudents = hasPermission("students_import", fallbackManage);
  const canPrintAdmissionForm = hasPermission("students_admission_form", canViewStudents);
  const canUseTcOperations = isAdmin || isSuperadmin || isFrontoffice || isCoordinator;

  return {
    roles,
    permissions,
    isAdmin,
    isSuperadmin,
    isPrincipal,
    isAccounts,
    isFrontoffice,
    isCoordinator,
    canViewStudents,
    canAddStudents,
    canEditStudents,
    canDeleteStudents,
    canToggleStudentStatus,
    canUploadStudentPhoto,
    canExportStudents,
    canImportStudents,
    canPrintAdmissionForm,
    canUseTcOperations,
    canManageStudents: canAddStudents || canEditStudents,
  };
};

// --- helpers: photo URL + fallback SVG ---
const apiBase = (() => {
  const b = api?.defaults?.baseURL;
  return b ? b.replace(/\/+$/, "") : window.location.origin;
})();

const buildPhotoURL = (fileName) =>
  fileName ? `${apiBase}/uploads/photoes/students/${encodeURIComponent(fileName)}` : "";

// Small neutral "no photo" SVG placeholder
const NO_PHOTO_SVG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64">
       <rect width="100%" height="100%" fill="#f8f9fa"/>
       <circle cx="32" cy="24" r="14" fill="#e9ecef"/>
       <rect x="10" y="42" width="44" height="14" rx="7" fill="#e9ecef"/>
     </svg>`
  );

// fetch next admission-number suggestion from backend
const fetchNextAdmissionNumber = async (prefix) => {
  try {
    const url = prefix
      ? `/students/next-admission-number?prefix=${encodeURIComponent(prefix)}`
      : `/students/next-admission-number`;
    const { data } = await api.get(url);
    return data?.suggestion || "";
  } catch (e) {
    console.warn("fetchNextAdmissionNumber failed:", e);
    return "";
  }
};

// small lookups for dropdowns
const STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jammu & Kashmir (J&K)",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Delhi",
  "Puducherry",
  "Other",
];

const CATEGORIES = ["General", "OBC", "SC", "ST", "EWS", "Other"];
const RELIGIONS = ["Hindu", "Muslim", "Sikh", "Christian", "Buddhist", "Jain", "Other"];

// ✅ gender options
const GENDERS = ["Male", "Female", "Other"];

// blood groups list
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

// colors for sibling badges
const SIBLING_COLORS = [
  { bg: "#6366f1", text: "#fff" },
  { bg: "#0ea5e9", text: "#fff" },
  { bg: "#22c55e", text: "#fff" },
  { bg: "#f97316", text: "#fff" },
];


// ✅ Sibling token helpers
// Important: sibling_id_1..4 often stores admission_number, even when numeric.
// So every frontend lookup must prefer admission_number before DB id.
const normalizeSiblingToken = (value) =>
  String(value ?? "")
    .replace(/^(ID|AN)\s*:\s*/i, "")
    .trim();

const findStudentBySiblingToken = (list, token) => {
  const normalized = normalizeSiblingToken(token);
  if (!normalized) return null;

  const arr = Array.isArray(list) ? list : [];

  return (
    arr.find((st) => String(st.admission_number || "").trim() === normalized) ||
    null
  );
};

const siblingDisplayToken = (token) => {
  const normalized = normalizeSiblingToken(token);
  return normalized ? `AN:${normalized}` : "Sibling";
};

const buildDirectPaymentPath = (student = {}) => {
  const admission = String(student.admission_number || "").trim();
  return admission ? `/direct-pay?adm=${encodeURIComponent(admission)}` : "";
};



const extractAdmissionTypes = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.admissionTypes)) return data.admissionTypes;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const escapeHtmlAttr = (value = "") =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const normalizeAdmissionMasterCode = (type = {}) =>
  String(type?.code || type?.name || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");

const getLegacyAdmissionTypeValue = (type = {}) => {
  const code = normalizeAdmissionMasterCode(type);
  const name = String(type?.name || "").trim().toLowerCase();

  // Keep legacy DB enum safe: only New / Old are allowed in old column.
  if (code === "OLD" || name.includes("old") || name.includes("continuing")) {
    return "Old";
  }

  return "New";
};

const getAdmissionTypeLabel = (student = {}, admissionTypes = []) => {
  const direct =
    student?.AdmissionType?.name ||
    student?.admissionTypeName ||
    student?.admission_type_name ||
    student?.admission_type_label ||
    student?.admissionType?.name ||
    "";

  if (direct) return direct;

  const id = student?.admission_type_id;
  if (id !== undefined && id !== null && String(id).trim() !== "") {
    const matched = admissionTypes.find((type) => String(type.id) === String(id));
    if (matched?.name) return matched.name;
  }

  return student?.admission_type || "-";
};

const getAdmissionTypeBadgeClass = (label = "") => {
  const value = String(label || "").toLowerCase();
  if (value.includes("new") || value.includes("late")) return "bg-success";
  if (value.includes("old") || value.includes("continuing")) return "bg-warning text-dark";
  if (value.includes("transfer")) return "bg-primary";
  if (value.includes("rte")) return "bg-info text-dark";
  return "bg-secondary";
};

const EXPORT_COLUMN_OPTIONS = [
  { key: "admission_number", label: "Admission Number" },
  { key: "name", label: "Student Name" },
  { key: "father_name", label: "Father Name" },
  { key: "mother_name", label: "Mother Name" },
  { key: "father_phone", label: "Father Phone" },
  { key: "mother_phone", label: "Mother Phone" },
  { key: "class_name", label: "Class" },
  { key: "section_name", label: "Section" },
  { key: "session_name", label: "Session" },
  { key: "house_name", label: "House" },
  { key: "address", label: "Address" },
  { key: "Date_Of_Birth", label: "Date of Birth" },
  { key: "gender", label: "Gender" },
  { key: "bus_gender", label: "Bus Gender" },
  { key: "b_group", label: "Blood Group" },
  { key: "state", label: "State" },
  { key: "category", label: "Category" },
  { key: "religion", label: "Religion" },
  { key: "aadhaar_number", label: "Aadhaar Number" },
  { key: "date_of_admission", label: "Date of Admission" },
  { key: "date_of_withdraw", label: "Date of Withdrawal" },
  { key: "bus_service", label: "Bus Service" },
  { key: "route_name", label: "Route Name" },
  { key: "route_cost", label: "Route Cost" },
  { key: "status", label: "Status" },
  { key: "admission_type", label: "Admission Type" },
  { key: "roll_number", label: "Roll Number" },
  { key: "pen_number", label: "PEN Number" },
  { key: "prev_school_name", label: "Previous School Name" },
  { key: "prev_school_address", label: "Previous School Address" },
  { key: "prev_class", label: "Previous Class" },
  { key: "prev_admission_no", label: "Previous Admission No" },
];

const DEFAULT_EXPORT_COLUMNS = [
  "admission_number",
  "name",
  "father_name",
  "mother_name",
  "father_phone",
  "class_name",
  "section_name",
  "session_name",
  "status",
];

const Students = () => {
  const navigate = useNavigate();
  const {
    roles,
    isAdmin,
    isSuperadmin,
    isPrincipal,
    canManageStudents,
    canAddStudents,
    canEditStudents,
    canDeleteStudents,
    canToggleStudentStatus,
    canUploadStudentPhoto,
    canExportStudents,
    canImportStudents,
    canPrintAdmissionForm,
    canUseTcOperations,
  } = getPermissionFlags();

  const canAddFeeOverrides =
    isAdmin || isSuperadmin || roles.includes("accounts");
  const canAddTransportOverrides =
    canAddFeeOverrides || roles.includes("transport");

  // data lists
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [concessions, setConcessions] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [transportations, setTransportations] = useState([]);
  const [houses, setHouses] = useState([]);
  const [admissionTypes, setAdmissionTypes] = useState([]);

  // transport helpers
  const transportById = useMemo(() => {
    const m = new Map();
    transportations.forEach((t) => m.set(String(t.id), t));
    return m;
  }, [transportations]);

  const formatTransport = (t) => {
    if (!t) return "-";
    const vill = t.Villages || t.village || t.villages || "";
    const cost = t.Cost ?? t.cost;
    return vill ? `${vill}${cost ? ` — ₹${cost}` : ""}` : cost ? `₹${cost}` : "-";
  };

  const formatTransportById = (id) => {
    if (!id && id !== 0) return "-";
    const t = transportById.get(String(id));
    return formatTransport(t);
  };

  const fetchHouses = async () => {
    try {
      const { data } = await api.get("/houses");
      setHouses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("fetchHouses:", err);
      setHouses([]);
    }
  };

  const fetchAdmissionTypes = async () => {
    try {
      const { data } = await api.get("/admission-types/active");
      const list = extractAdmissionTypes(data).filter((type) => type?.is_active !== false);
      setAdmissionTypes(list);
      return list;
    } catch (err) {
      console.error("fetchAdmissionTypes:", err);
      setAdmissionTypes([]);
      return [];
    }
  };

  // UI state
  const [search, setSearch] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  // Keep active and inactive records segregated. Active is the default view.
  const [selectedStatus, setSelectedStatus] = useState("enabled");
  const [selectedSessionFilter, setSelectedSessionFilter] = useState("");
  const [hasSiblingFilter, setHasSiblingFilter] = useState("");
  const [importing, setImporting] = useState(false);
  const [showImportPanel, setShowImportPanel] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState("");
  const [importError, setImportError] = useState("");
  const [openActionMenuId, setOpenActionMenuId] = useState(null);

  // ✅ Columns mode toggle (Compact / Full) with persistence
  const [showAllColumns, setShowAllColumns] = useState(() => {
    try {
      return localStorage.getItem("students_showAllColumns") === "1";
    } catch {
      return false;
    }
  });

  const toggleColumnsMode = () => {
    setShowAllColumns((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("students_showAllColumns", next ? "1" : "0");
      } catch {}
      return next;
    });
  };

  const isCompact = !showAllColumns;

  // fetch lists
  const fetchStudents = async ({ sessionId, classId } = {}) => {
    const effectiveSessionId =
      typeof sessionId === "undefined" ? selectedSessionFilter : sessionId;
    const effectiveClassId =
      typeof classId === "undefined" ? selectedClass : classId;

    try {
      let url = "/students";

      if (effectiveSessionId) {
        const params = new URLSearchParams();
        params.append("session_id", effectiveSessionId);
        if (effectiveClassId) params.append("class_id", effectiveClassId);
        url = "/students/by-session?" + params.toString();
      }

      const { data } = await api.get(url);
      setStudents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("fetchStudents:", err);
      Swal.fire("Error", "Failed to fetch students", "error");
    }
  };

  const fetchClasses = async () => {
    try {
      const { data } = await api.get("/classes");
      setClasses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("fetchClasses:", err);
    }
  };

  const fetchSections = async () => {
    try {
      const resp = await api.get("/sections");

      // support both shapes:
      // 1) [ ... ]
      // 2) { ok: true, data: [ ... ] }
      const list = Array.isArray(resp.data) ? resp.data : resp.data?.data;

      setSections(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("fetchSections:", err);
      setSections([]);
    }
  };

  const fetchConcessions = async () => {
    try {
      const { data } = await api.get("/concessions");
      setConcessions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("fetchConcessions:", err);
    }
  };

  const fetchSessions = async () => {
    try {
      const { data } = await api.get("/sessions");
      setSessions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("fetchSessions:", err);
    }
  };

  const fetchTransportations = async () => {
    try {
      // ✅ fetch all routes (all sessions)
      const { data } = await api.get("/transportations");
      setTransportations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("fetchTransportations:", err);
      setTransportations([]);
    }
  };

  useEffect(() => {
    fetchStudents({ sessionId: selectedSessionFilter, classId: selectedClass });
    fetchClasses();
    fetchSections();
    fetchSessions();
    fetchTransportations();
    fetchHouses();
    fetchAdmissionTypes();
    if (canManageStudents) fetchConcessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageStudents]);

  useEffect(() => {
    fetchStudents({ sessionId: selectedSessionFilter, classId: selectedClass });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSessionFilter]);

  /* ============================================================
   * ✅ House auto-suggest (lowest strength) helpers
   * ============================================================ */

  const getHouseStrengthMap = (sessionIdOrEmpty) => {
    const map = new Map(); // houseId -> count
    houses.forEach((h) => map.set(String(h.id), 0));

    const eligible = students.filter((st) => {
      if (st.status && String(st.status).toLowerCase() === "disabled") return false;
      if (sessionIdOrEmpty) return String(st.session_id || "") === String(sessionIdOrEmpty);
      return true;
    });

    eligible.forEach((st) => {
      const hid = st.house_id;
      if (!hid) return;
      const key = String(hid);
      map.set(key, (map.get(key) || 0) + 1);
    });

    return map;
  };

  const getLowestStrengthHouseId = (sessionIdOrEmpty) => {
    if (!houses.length) return "";
    const strength = getHouseStrengthMap(sessionIdOrEmpty);

    let bestId = String(houses[0].id);
    let bestCount = strength.get(bestId) ?? 0;

    houses.forEach((h) => {
      const id = String(h.id);
      const c = strength.get(id) ?? 0;
      if (c < bestCount) {
        bestId = id;
        bestCount = c;
      }
    });

    return bestId;
  };

  // toggle status
  const toggleStudentStatus = async (student) => {
    if (!canToggleStudentStatus) return;
    const newStatus = student.status === "enabled" ? "disabled" : "enabled";

    const result = await Swal.fire({
      title: `Confirm Status Change`,
      text: `Set ${student.name}'s status to ${newStatus.toUpperCase()}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Update",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    try {
      await api.put(`/students/toggle/${student.id}`, { status: newStatus });
      Swal.fire("Updated", `Status changed to ${newStatus.toUpperCase()}`, "success");
      fetchStudents();
    } catch (err) {
      console.error("toggleStudentStatus:", err);
      Swal.fire("Error", err.response?.data?.error || "Failed to update status", "error");
    }
  };

  // delete
  const handleDelete = async (id, name) => {
    if (!canDeleteStudents) return;
    const result = await Swal.fire({
      title: "Confirm Deletion",
      text: `Permanently delete ${name}? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    try {
      await api.delete(`/students/delete/${id}`);
      Swal.fire("Deleted", "Student record removed successfully.", "success");
      fetchStudents();
    } catch (err) {
      console.error("handleDelete:", err);
      Swal.fire("Error", "Failed to delete student", "error");
    }
  };

  // PHOTO: choose file + upload
  const promptAndUploadPhoto = (student) => {
    if (!canUploadStudentPhoto) return;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const fd = new FormData();
      fd.append("photo", file);
      if (student.admission_number) fd.append("admission_number", student.admission_number);

      try {
        Swal.showLoading();
        const { data } = await api.post(`/students/${student.id}/photo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        Swal.close();
        await Swal.fire("Success", data?.message || "Photo uploaded successfully", "success");
        fetchStudents();
      } catch (err) {
        console.error("upload photo error:", err);
        Swal.close();
        Swal.fire("Error", err.response?.data?.message || "Failed to upload photo", "error");
      }
    };
    input.click();
  };

  // Print Admission Form
  const handlePrintAdmissionForm = async (student) => {
    if (!canPrintAdmissionForm || !student?.id) return;

    try {
      const resp = await api.get(`/students/${student.id}/admission-form`, {
        responseType: "blob",
      });

      const blob = new Blob([resp.data], {
        type: resp.headers["content-type"] || "application/pdf",
      });
      const url = URL.createObjectURL(blob);

      const win = window.open(url, "_blank");
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.download = `Admission_Form_${student.admission_number || student.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }

      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      console.error("handlePrintAdmissionForm:", err);
      Swal.fire(
        "Error",
        err.response?.data?.message || "Failed to generate admission form. Please try again.",
        "error"
      );
    }
  };

  // ---------------- ADD / EDIT FORM ----------------
  const showStudentForm = async (mode = "add", student = null) => {
    if (mode === "add" && !canAddStudents) return;
    if (mode === "edit" && !canEditStudents) return;
    const isEdit = mode === "edit";
    const s = student || {};
    await fetchClasses();
    await fetchSections();
    await fetchSessions();
    await fetchTransportations();
    await fetchHouses();
    const admissionTypesData = admissionTypes.length ? admissionTypes : await fetchAdmissionTypes();
    let feeHeadingsData = [];
    let existingFeeOverrides = [];
    let existingTransportOverrides = [];
    let studentFeeDetails = [];
    if (canAddFeeOverrides || canAddTransportOverrides) {
      try {
        const { data } = await api.get("/fee-headings");
        feeHeadingsData = Array.isArray(data) ? data : data?.data || [];
      } catch (err) {
        console.error("fetch fee headings for student overrides:", err);
      }
    }
    if (isEdit && s.id && s.session_id) {
      if (canAddFeeOverrides) {
        try {
          const { data } = await api.get(`/students/${s.id}/fee-details`, {
            params: { session_id: s.session_id },
          });
          studentFeeDetails = Array.isArray(data?.feeDetails) ? data.feeDetails : [];
        } catch (err) {
          console.error("fetch student's normal fee structure:", err);
        }
        try {
          const { data } = await api.get(`/student-fee-structures/student/${s.id}`, {
            params: { session_id: s.session_id },
          });
          const rows = Array.isArray(data) ? data : data?.data || [];
          existingFeeOverrides = rows.filter(
            (row) => String(row.sourceType || "").toLowerCase() === "override"
          );
        } catch (err) {
          if (err.response?.status !== 404) {
            console.error("fetch existing student fee overrides:", err);
          }
        }
      }
      if (canAddTransportOverrides) {
        try {
          const { data } = await api.get("/student-transport-fee-head-amounts", {
            params: { student_id: s.id, session_id: s.session_id },
          });
          existingTransportOverrides = Array.isArray(data) ? data : data?.data || [];
        } catch (err) {
          console.error("fetch existing transport fee overrides:", err);
        }
      }
    }
    await fetchStudents();
    if (canManageStudents) await fetchConcessions();

    const feeOverrideByHead = new Map(
      existingFeeOverrides.map((row) => [String(row.fee_heading_id), row])
    );
    const transportOverrideByHead = new Map(
      existingTransportOverrides.map((row) => [String(row.fee_head_id), row])
    );
    const feeDetailByHead = new Map(
      studentFeeDetails.map((row) => [String(row.fee_heading_id), row])
    );
    const feeOverrideDisplayHeads =
      isEdit && studentFeeDetails.length
        ? studentFeeDetails.map((row) => ({
            id: row.fee_heading_id,
            fee_heading: row.fee_heading,
          }))
        : feeHeadingsData;
    const isTransportApplicable = (value) =>
      value === true ||
      value === 1 ||
      String(value || "").trim().toLowerCase() === "yes" ||
      String(value || "").trim().toLowerCase() === "true";
    const transportOverrideDisplayHeads = studentFeeDetails
      .filter((row) => isTransportApplicable(row.transportApplicable))
      .map((row) => ({
        id: row.fee_heading_id,
        fee_heading: row.fee_heading,
      }));

    // helper: pick first non-empty field from multiple possible keys
    const pickField = (obj, keys, fallback = "") => {
      for (const k of keys) {
        if (obj && obj[k] != null && String(obj[k]).trim() !== "") {
          return String(obj[k]).trim();
        }
      }
      return fallback;
    };

    // normalize from backend: support different key names / casing
    const existingBG = pickField(s, ["b_group", "B_group", "B_GROUP", "blood_group", "Blood_Group"]);
    const existingState = pickField(s, ["state", "State", "STATE"]);

    // ✅ gender normalizers
    const existingGender = pickField(s, ["gender", "Gender", "GENDER"]);
    const existingBusGender = pickField(s, ["bus_gender", "busGender", "BusGender", "BUS_GENDER"]);

    let nextSuggestion = "";
    if (!isEdit) {
      nextSuggestion = await fetchNextAdmissionNumber();
    }

    const classOptions = `<option value="">Select Class</option>${classes
      .map(
        (c) =>
          `<option value="${c.id}" ${c.id === s.class_id ? "selected" : ""}>${c.class_name}</option>`
      )
      .join("")}`;

    const sectionOptionsAll = sections.map((sec) => ({
      id: sec.id,
      name: sec.section_name,
      class_id: sec.class_id,
    }));

    const concessionOptions = `<option value="">Select Concession</option>${concessions
      .map(
        (c) =>
          `<option value="${c.id}" ${c.id === s.concession_id ? "selected" : ""}>${c.concession_name}</option>`
      )
      .join("")}`;

    const sessionOptions = `<option value="">Select Session</option>${sessions
      .map(
        (ss) =>
          `<option value="${ss.id}" ${ss.id === s.session_id ? "selected" : ""}>${ss.name}</option>`
      )
      .join("")}`;

    const transportOptions = `<option value="">No Transport</option>${transportations
      .map((t) => {
        const label = (t?.Villages || "").replace(/"/g, "&quot;");
        const cost = t?.Cost ?? "";
        return `<option value="${t.id}" data-cost="${Number(cost || 0)}" ${t.id === s.route_id ? "selected" : ""}>${label}${
          cost ? ` — ₹${cost}` : ""
        }</option>`;
      })
      .join("")}`;

    // ----- Blood group options (case-insensitive, allow custom value) -----
    const currentBG = existingBG;
    let bgOptionsHtml = BLOOD_GROUPS.map((bg) => {
      const isSelected = currentBG && bg.toLowerCase() === currentBG.toLowerCase();
      return `<option value="${bg}" ${isSelected ? "selected" : ""}>${bg}</option>`;
    }).join("");

    if (currentBG && !BLOOD_GROUPS.some((bg) => bg.toLowerCase() === currentBG.toLowerCase())) {
      bgOptionsHtml += `<option value="${currentBG}" selected>${currentBG}</option>`;
    }

    // ----- State options (case-insensitive, allow custom value) -----
    const currentState = existingState;
    let stateOptionsHtml = STATES.map((st) => {
      const isSelected = currentState && st.toLowerCase() === currentState.toLowerCase();
      return `<option value="${st}" ${isSelected ? "selected" : ""}>${st}</option>`;
    }).join("");

    if (currentState && !STATES.some((st) => st.toLowerCase() === currentState.toLowerCase())) {
      stateOptionsHtml += `<option value="${currentState}" selected>${currentState}</option>`;
    }

    // ✅ gender options
    const genderOptionsHtml = [
      `<option value="">Select Gender</option>`,
      ...GENDERS.map((g) => {
        const sel = existingGender && g.toLowerCase() === existingGender.toLowerCase();
        return `<option value="${g}" ${sel ? "selected" : ""}>${g}</option>`;
      }),
    ].join("");

    const busGenderOptionsHtml = [
      `<option value="">Same as Gender</option>`,
      ...GENDERS.map((g) => {
        const sel = existingBusGender && g.toLowerCase() === existingBusGender.toLowerCase();
        return `<option value="${g}" ${sel ? "selected" : ""}>${g}</option>`;
      }),
    ].join("");

    // ✅ Dynamic admission type options
    const admissionTypeList = Array.isArray(admissionTypesData) ? admissionTypesData : [];
    const currentAdmissionTypeId =
      s.admission_type_id ||
      s.AdmissionType?.id ||
      admissionTypeList.find((type) =>
        String(type.name || "").trim().toLowerCase() ===
        String(s.admission_type || "").trim().toLowerCase()
      )?.id ||
      admissionTypeList.find((type) => String(type.code || "").toUpperCase() === "NEW")?.id ||
      admissionTypeList[0]?.id ||
      "";

    const admissionTypeOptionsHtml = [
      `<option value="">Select Admission Type</option>`,
      ...admissionTypeList.map((type) => {
        const selected = String(type.id) === String(currentAdmissionTypeId);
        return `<option value="${type.id}" data-name="${escapeHtmlAttr(type.name)}" data-code="${escapeHtmlAttr(type.code || "")}" ${selected ? "selected" : ""}>${escapeHtmlAttr(type.name)}</option>`;
      }),
    ].join("");

    // ✅ default house for ADD mode (lowest strength)
    const defaultSessionForSuggest = s.session_id ? String(s.session_id) : "";
    const suggestedHouseId = !isEdit ? getLowestStrengthHouseId(defaultSessionForSuggest) : "";

    // ✅ Compact title bar
    const headerTitle = isEdit ? "Edit Student" : "Add Student";

    // ---------- HTML (compact + sticky mini header) ----------
    const html = `
      <style>
        .swal2-popup.student-swal {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          max-width: 1040px !important;
          width: 100% !important;
          padding: 0 !important;
          border-radius: 18px !important;
          overflow: hidden;
          box-shadow: 0 24px 80px var(--edb-shell-shadow) !important;
        }
        .student-topbar {
          position: sticky;
          top: 0;
          z-index: 50;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 12px 16px;
          background: linear-gradient(135deg, var(--edb-surface) 0%, var(--edb-surface) 58%, var(--edb-surface) 100%);
          border-bottom: 1px solid var(--edb-border);
        }
        .student-topbar .title {
          font-weight: 800;
          font-size: 15px;
          color: var(--edb-text);
          margin: 0;
        }
        .student-topbar .xbtn {
          border: 1px solid var(--edb-border);
          background: var(--edb-surface);
          border-radius: 10px;
          padding: 6px 10px;
          font-size: 12px;
          cursor: pointer;
        }
        .student-topbar .xbtn:hover { background: var(--edb-surface); }

        .student-form-wrapper {
          max-height: 64vh;
          overflow-y: auto;
          padding: 10px 12px 0;
        }
        .tabbar {
          display: flex;
          gap: 6px;
          margin: 0 0 10px;
          border-bottom: 1px solid var(--edb-border);
          padding: 0 2px 8px;
          overflow-x: auto;
        }
        .tabbtn {
          padding: 6px 10px;
          border: 1px solid var(--edb-border);
          background: var(--edb-surface);
          cursor: pointer;
          font-weight: 600;
          color: var(--edb-muted-text);
          border-radius: 10px;
          transition: all 0.15s ease;
          white-space: nowrap;
          font-size: 12px;
        }
        .tabbtn:hover { color: var(--edb-text); background-color: var(--edb-surface); }
        .tabbtn.active {
          color: var(--edb-text);
          border-color: var(--edb-primary);
          background: var(--edb-surface);
        }
        .tabpane { display: none; }
        .tabpane.active { display: block; }

        .form-container {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px 12px;
          background: var(--edb-surface);
          padding: 12px;
          border-radius: 12px;
          border: 1px solid var(--edb-border);
        }
        .full-row { grid-column: 1 / -1; }

        .form-field {
          width: 100%;
          padding: 7px 10px;
          border: 1px solid var(--edb-border);
          border-radius: 12px;
          font-size: 13px;
          background: var(--edb-surface);
        }
        .form-field:focus {
          border-color: var(--edb-primary);
          outline: 0;
          box-shadow: 0 0 0 0.12rem var(--edb-shell-shadow);
        }
        .form-label {
          font-weight: 700;
          color: var(--edb-text);
          margin-bottom: 4px;
          display: block;
          font-size: 12px;
        }
        .required { color: #dc2626; }
        .hint {
          color: var(--edb-muted-text);
          font-size: 11px;
          margin-top: 4px;
          font-style: italic;
        }
        .hint.small {
          font-size: 10.5px;
        }
        .sibling-block {
          border: 1px dashed var(--edb-border);
          padding: 10px;
          border-radius: 12px;
          margin-bottom: 8px;
          background: linear-gradient(180deg, var(--edb-surface) 0%, var(--edb-surface) 100%);
        }
        .override-note {
          grid-column: 1 / -1;
          padding: 9px 11px;
          border: 1px solid var(--edb-border);
          border-radius: 10px;
          background: var(--edb-surface);
          color: var(--edb-primary-text);
          font-size: 11.5px;
          line-height: 1.45;
        }
        .override-table-wrap {
          grid-column: 1 / -1;
          max-height: 310px;
          overflow: auto;
          border: 1px solid var(--edb-border);
          border-radius: 10px;
        }
        .override-table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .override-table th, .override-table td {
          padding: 7px 8px;
          border-bottom: 1px solid var(--edb-border);
          text-align: left;
          vertical-align: middle;
        }
        .override-table th { position: sticky; top: 0; background: var(--edb-surface); z-index: 1; }
        .override-table .amount-input { min-width: 120px; }
        .sibling-row {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          align-items: end;
        }

        .swal2-actions {
          margin: 10px 0 14px !important;
          padding: 0 12px;
        }

        @media (max-width: 768px) {
          .form-container { grid-template-columns: 1fr; }
        }
      </style>

      <div class="student-topbar">
        <div class="title">${headerTitle}</div>
        <button type="button" class="xbtn" id="studentSwalCloseBtn">✕</button>
      </div>

      <div class="student-form-wrapper">
        <div class="tabbar">
          <button class="tabbtn active" data-tab="mandatory">Basic</button>
          <button class="tabbtn" data-tab="personal">Personal</button>
          <button class="tabbtn" data-tab="contact">Contact</button>
          <button class="tabbtn" data-tab="transport">Transport</button>
          ${canAddTransportOverrides ? `<button class="tabbtn" data-tab="transportoverride">Transport Override</button>` : ""}
          ${canAddFeeOverrides ? `<button class="tabbtn" data-tab="feeoverride">Fee Override</button>` : ""}
          <button class="tabbtn" data-tab="siblings">Siblings</button>
          <button class="tabbtn" data-tab="prevschool">Previous</button>
        </div>

        <!-- Basic Info -->
        <div class="tabpane active" id="pane-mandatory">
          <div class="form-container">
            <div>
              <label class="form-label">Full Name <span class="required">*</span></label>
              <input id="f_name" class="form-field" value="${(s.name || "").replace(/"/g, "&quot;")}" required />
            </div>

            <div>
              <label class="form-label">Admission Number</label>
              <input id="f_admission_number" class="form-field"
                value="${isEdit ? s.admission_number || "" : s.admission_number || nextSuggestion || ""}" />
              <div class="hint">Leave empty for auto-generation</div>
            </div>

            <div>
              <label class="form-label">Class <span class="required">*</span></label>
              <select id="f_class_id" class="form-field" required>${classOptions}</select>
            </div>

            <div>
              <label class="form-label">Section <span class="required">*</span></label>
              <select id="f_section_id" class="form-field" required>
                <option value="">Select Section</option>
                ${sections
                  .map(
                    (sec) =>
                      `<option value="${sec.id}" data-class="${sec.class_id}" ${
                        sec.id === s.section_id ? "selected" : ""
                      }>${sec.section_name}</option>`
                  )
                  .join("")}
              </select>
            </div>

            <div>
              <label class="form-label">Session</label>
              <select id="f_session_id" class="form-field">${sessionOptions}</select>
            </div>

            <div>
              <label class="form-label">House</label>
              <select id="f_house_id" class="form-field">
                <option value="">Select House</option>
                ${houses
                  .map((h) => {
                    const selected =
                      (s.house_id && String(h.id) === String(s.house_id)) ||
                      (!isEdit && !s.house_id && suggestedHouseId && String(h.id) === String(suggestedHouseId));
                    return `<option value="${h.id}" ${selected ? "selected" : ""}>${h.house_name}${
                      h.color ? ` (${h.color})` : ""
                    }</option>`;
                  })
                  .join("")}
              </select>
              ${!isEdit ? `<div class="hint small">Auto-selected lowest strength house (you can change).</div>` : ""}
            </div>

            ${
              canManageStudents
                ? `<div class="full-row">
                    <label class="form-label">Concession Type</label>
                    <select id="f_concession_id" class="form-field">
                      ${concessionOptions}
                    </select>
                  </div>`
                : ""
            }

            <div>
              <label class="form-label">Admission Type</label>
              <select id="f_admission_type_id" class="form-field">
                ${admissionTypeOptionsHtml}
              </select>
              <div class="hint">Dynamic from Admission Types master.</div>
            </div>

            <div>
              <label class="form-label">Roll Number</label>
              <input id="f_roll_number" class="form-field" value="${s.roll_number || ""}" type="number" min="1" />
            </div>
          </div>
        </div>

        <!-- Personal -->
        <div class="tabpane" id="pane-personal">
          <div class="form-container">
            <div>
              <label class="form-label">Father's Name</label>
              <input id="f_father_name" class="form-field" value="${(s.father_name || "").replace(/"/g, "&quot;")}" />
            </div>

            <div>
              <label class="form-label">Mother's Name</label>
              <input id="f_mother_name" class="form-field" value="${(s.mother_name || "").replace(/"/g, "&quot;")}" />
            </div>

            <div>
              <label class="form-label">Gender</label>
              <select id="f_gender" class="form-field">
                ${genderOptionsHtml}
              </select>
            </div>

            <div>
              <label class="form-label">Date of Birth</label>
              <input id="f_DOB" class="form-field" value="${s.Date_Of_Birth || ""}" type="date" />
            </div>

            <div>
              <label class="form-label">Blood Group</label>
              <select id="f_b_group" class="form-field">
                <option value="">Select Blood Group</option>
                ${bgOptionsHtml}
              </select>
            </div>

            <div>
              <label class="form-label">Date of Admission</label>
              <input id="f_date_of_admission" class="form-field" value="${s.date_of_admission || ""}" type="date" />
            </div>

            <div>
              <label class="form-label">Date of Withdrawal</label>
              <input id="f_date_of_withdraw" class="form-field" value="${s.date_of_withdraw || ""}" type="date" />
            </div>

            <div>
              <label class="form-label">PEN Number</label>
              <input id="f_pen_number" class="form-field" value="${s.pen_number || ""}" />
            </div>

            <div>
              <label class="form-label">State</label>
              <select id="f_state" class="form-field">
                <option value="">Select State</option>
                ${stateOptionsHtml}
              </select>
            </div>

            <div>
              <label class="form-label">Category</label>
              <select id="f_category" class="form-field">
                <option value="">Select Category</option>
                ${CATEGORIES.map(
                  (c) => `<option value="${c}" ${c === (s.category || "") ? "selected" : ""}>${c}</option>`
                ).join("")}
              </select>
            </div>

            <div>
              <label class="form-label">Religion</label>
              <select id="f_religion" class="form-field">
                <option value="">Select Religion</option>
                ${RELIGIONS.map(
                  (r) => `<option value="${r}" ${r === (s.religion || "") ? "selected" : ""}>${r}</option>`
                ).join("")}
              </select>
            </div>

            <div class="full-row">
              <label class="form-label">Residential Address</label>
              <textarea id="f_address" class="form-field" rows="3">${(s.address || "").replace(
                /</g,
                "&lt;"
              )}</textarea>
            </div>
          </div>
        </div>

        <!-- Contact -->
        <div class="tabpane" id="pane-contact">
          <div class="form-container">
            <div>
              <label class="form-label">Father's Phone</label>
              <input id="f_father_phone" class="form-field" value="${s.father_phone || ""}" type="tel" maxlength="15" />
            </div>
            <div>
              <label class="form-label">Mother's Phone</label>
              <input id="f_mother_phone" class="form-field" value="${s.mother_phone || ""}" type="tel" maxlength="15" />
            </div>
            <div>
              <label class="form-label">Aadhaar Number</label>
              <input id="f_aadhaar" class="form-field" value="${s.aadhaar_number || ""}" type="text" maxlength="12" />
            </div>
            <div>
              <label class="form-label">Public Visibility</label>
              <select id="f_visible" class="form-field">
                <option value="1" ${s.visible ? "selected" : ""}>Yes</option>
                <option value="0" ${!s.visible ? "selected" : ""}>No</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Transport -->
        <div class="tabpane" id="pane-transport">
          <div class="form-container">
            <div>
              <label class="form-label">Bus Service Fee (₹)</label>
              <input id="f_bus_service" class="form-field" value="${s.bus_service || ""}" type="number" min="0" />
            </div>

            <div>
              <label class="form-label">Transport Route</label>
              <select id="f_route_id" class="form-field">
                ${transportOptions}
              </select>
              <div class="hint">Optional: Select a transport route</div>
            </div>

            <div>
              <label class="form-label">Bus Gender</label>
              <select id="f_bus_gender" class="form-field">
                ${busGenderOptionsHtml}
              </select>
              <div class="hint">Optional: if bus seating uses gender rule</div>
            </div>

            <div>
              <label class="form-label">—</label>
              <div class="hint">Tip: keep empty to follow normal class seating.</div>
            </div>
          </div>
        </div>

        ${
          canAddTransportOverrides
            ? `<!-- Transport Fee Override -->
        <div class="tabpane" id="pane-transportoverride">
          <div class="form-container">
            <div class="override-note">
              Only transport-applicable fee heads are shown. Review the normal route charge and already paid/concession amount before entering an override. Uncheck an existing override to remove it on Update.
            </div>
            <div class="override-table-wrap">
              <table class="override-table">
                <thead><tr><th style="width:60px">Override</th><th>Fee Head</th><th style="width:130px">Normal Transport</th><th style="width:130px">Paid + Concession</th><th style="width:170px">Current / New Override</th></tr></thead>
                <tbody id="transportOverrideRows">
                  ${
                    transportOverrideDisplayHeads.length
                      ? transportOverrideDisplayHeads
                          .map((head, index) => {
                            const existing = transportOverrideByHead.get(String(head.id));
                            const detail = feeDetailByHead.get(String(head.id));
                            const normalTransport = Number(detail?.normal_van_fee_due || 0);
                            const adjusted = Number(detail?.total_van_adjusted || 0);
                            return `<tr>
                              <td><input type="checkbox" class="transport-override-check" data-index="${index}" data-existing-id="${existing?.id || ""}" ${existing ? "checked" : ""} /></td>
                              <td>${escapeHtmlAttr(head.fee_heading || `Fee Head ${head.id}`)}${existing ? ` <span style="color:#2563eb">(existing)</span>` : ""}</td>
                              <td>₹${normalTransport.toFixed(2)}</td>
                              <td>₹${adjusted.toFixed(2)}</td>
                              <td><input type="number" min="${adjusted}" step="0.01" class="form-field amount-input transport-override-amount" data-index="${index}" data-head-id="${head.id}" data-transportation-id="${existing?.transportation_id || ""}" value="${existing?.amount ?? 0}" ${existing ? "" : "disabled"} /></td>
                            </tr>`;
                          })
                          .join("")
                      : `<tr><td colspan="5" style="text-align:center;color:#6b7280">${isEdit ? "No transport-applicable fee heads were found for this student and session." : "Select class and session to load transport-applicable fee heads."}</td></tr>`
                  }
                </tbody>
              </table>
            </div>
            <div class="full-row">
              <label class="form-label">Remarks</label>
              <input id="f_transport_override_remarks" class="form-field" value="${escapeHtmlAttr(existingTransportOverrides[0]?.remarks || "Admission transport fee override")}" />
            </div>
          </div>
        </div>`
            : ""
        }

        ${
          canAddFeeOverrides
            ? `<!-- Student Fee Override -->
        <div class="tabpane" id="pane-feeoverride">
          <div class="form-container">
            <div class="override-note">
              The normal fee structure is shown first. Tick only the heads you want to override; ₹0 is an intentional waiver. Uncheck an existing override to remove it on Update.
            </div>
            <div class="override-table-wrap">
              <table class="override-table">
                <thead><tr><th style="width:60px">Override</th><th>Fee Head</th><th style="width:130px">Normal Fee</th><th style="width:130px">Paid + Concession</th><th style="width:170px">Current / New Override</th></tr></thead>
                <tbody>
                  ${
                    feeOverrideDisplayHeads.length
                      ? feeOverrideDisplayHeads
                          .map((head, index) => {
                            const existing = feeOverrideByHead.get(String(head.id));
                            const detail = feeDetailByHead.get(String(head.id));
                            const normalFee = detail?.normal_fee_due;
                            const adjusted = Number(detail?.total_adjusted || 0);
                            return `<tr>
                              <td><input type="checkbox" class="fee-override-check" data-index="${index}" data-existing-id="${existing?.id || ""}" ${existing ? "checked" : ""} /></td>
                              <td>${escapeHtmlAttr(head.fee_heading || `Fee Head ${head.id}`)}${existing ? ` <span style="color:#2563eb">(existing)</span>` : ""}</td>
                              <td>${normalFee !== undefined && normalFee !== null ? `₹${Number(normalFee).toFixed(2)}` : "—"}</td>
                              <td>₹${adjusted.toFixed(2)}</td>
                              <td><input type="number" min="${adjusted}" step="0.01" class="form-field amount-input fee-override-amount" data-index="${index}" data-head-id="${head.id}" value="${existing?.feeDue ?? 0}" ${existing ? "" : "disabled"} /></td>
                            </tr>`;
                          })
                          .join("")
                      : `<tr><td colspan="5" style="text-align:center;color:#6b7280">No applicable fee structure was found for this student and session.</td></tr>`
                  }
                </tbody>
              </table>
            </div>
            <div class="full-row">
              <label class="form-label">Reason / Remarks</label>
              <input id="f_fee_override_remarks" class="form-field" value="${escapeHtmlAttr(existingFeeOverrides[0]?.remarks || "Admission fee adjustment")}" />
            </div>
          </div>
        </div>`
            : ""
        }

        <!-- Siblings -->
        <div class="tabpane" id="pane-siblings">
          <div class="form-container">
            <div class="full-row" style="background:#f8fafc;border:1px solid #e5e7eb;border-radius:12px;padding:10px 12px;color:#475569;font-size:12px;">
              <strong>Sibling mapping:</strong> admission number is used first to prevent numeric admission numbers from matching wrong database IDs.
            </div>
            ${[1, 2, 3, 4]
              .map(
                (slot) => `
              <div class="full-row sibling-block" data-slot="${slot}">
                <label class="form-label">Sibling ${slot}</label>
                <div class="sibling-row">
                  <select id="sib_class_${slot}" class="form-field">
                    <option value="">Select Class</option>
                    ${classes
                      .map(
                        (c) =>
                          `<option value="${c.id}" ${
                            c.id === (s["sibling_class_" + slot] || "") ? "selected" : ""
                          }>${c.class_name}</option>`
                      )
                      .join("")}
                  </select>

                  <select id="sib_section_${slot}" class="form-field">
                    <option value="">All Sections</option>
                    ${sections
                      .map(
                        (sec) =>
                          `<option value="${sec.id}" data-class="${sec.class_id}" ${
                            sec.id === (s["sibling_section_" + slot] || "") ? "selected" : ""
                          }>${sec.section_name}</option>`
                      )
                      .join("")}
                  </select>

                  <select id="sib_student_${slot}" class="form-field">
                    <option value="">Select Student</option>
                    ${
                      s["sibling_id_" + slot]
                        ? `<option value="${s["sibling_id_" + slot]}" selected>${
                            (s["sibling_name_" + slot] || "Selected").replace(/"/g, "&quot;")
                          }${s["sibling_id_" + slot] ? ` (AN:${normalizeSiblingToken(s["sibling_id_" + slot])})` : ""}</option>`
                        : ""
                    }
                  </select>
                  <input id="f_sibling_id_${slot}" type="hidden" value="${s["sibling_id_" + slot] || ""}" />
                  <input id="f_sibling_name_${slot}" type="hidden" value="${(
                    s["sibling_name_" + slot] || ""
                  ).replace(/"/g, "&quot;")}" />
                </div>
              </div>
            `
              )
              .join("")}
          </div>
        </div>

        <!-- Previous School -->
        <div class="tabpane" id="pane-prevschool">
          <div class="form-container">
            <div class="full-row">
              <label class="form-label">Previous School Name</label>
              <input id="f_prev_school_name" class="form-field" value="${(s.prev_school_name || "").replace(
                /"/g,
                "&quot;"
              )}" />
            </div>
            <div class="full-row">
              <label class="form-label">Previous School Address</label>
              <textarea id="f_prev_school_address" class="form-field" rows="3">${(s.prev_school_address || "").replace(
                /</g,
                "&lt;"
              )}</textarea>
            </div>
            <div>
              <label class="form-label">Previous Class</label>
              <input id="f_prev_class" class="form-field" value="${(s.prev_class || "").replace(/"/g, "&quot;")}" />
            </div>
            <div>
              <label class="form-label">Previous Admission No.</label>
              <input id="f_prev_admission_no" class="form-field" value="${(s.prev_admission_no || "").replace(
                /"/g,
                "&quot;"
              )}" />
            </div>
          </div>
        </div>
      </div>
    `;

    const popup = await Swal.fire({
      title: "",
      icon: undefined,
      width: "1040px",
      html,
      showCancelButton: true,
      confirmButtonText: isEdit ? "Update" : "Add",
      cancelButtonText: "Cancel",
      focusConfirm: false,
      showLoaderOnConfirm: true,
      allowOutsideClick: false,
      customClass: { popup: "student-swal" },
      preConfirm: async () => {
        const rawRouteVal = document.getElementById("f_route_id")?.value;
        const routeVal = rawRouteVal === "" ? null : Number(rawRouteVal);

        const admissionTypeIdRaw = document.getElementById("f_admission_type_id")?.value || "";
        const selectedAdmissionType =
          admissionTypeList.find((type) => String(type.id) === String(admissionTypeIdRaw)) || null;
        const legacyAdmissionType = selectedAdmissionType
          ? getLegacyAdmissionTypeValue(selectedAdmissionType)
          : "New";

        const payload = {
          // Mandatory
          name: document.getElementById("f_name").value.trim(),
          admission_number: document.getElementById("f_admission_number").value.trim() || null,
          class_id: document.getElementById("f_class_id").value || null,
          section_id: document.getElementById("f_section_id").value || null,
          session_id: document.getElementById("f_session_id").value || null,
          house_id: document.getElementById("f_house_id")?.value || null,
          concession_id: document.getElementById("f_concession_id")?.value || null,
          admission_type_id: admissionTypeIdRaw ? Number(admissionTypeIdRaw) : null,
          admission_type: legacyAdmissionType,
          roll_number: document.getElementById("f_roll_number").value
            ? parseInt(document.getElementById("f_roll_number").value, 10)
            : null,

          // Personal
          father_name: document.getElementById("f_father_name").value.trim(),
          mother_name: document.getElementById("f_mother_name").value.trim(),

          // ✅ Gender
          gender: document.getElementById("f_gender")?.value || null,

          Date_Of_Birth: document.getElementById("f_DOB").value || null,
          date_of_admission: document.getElementById("f_date_of_admission").value || null,
          date_of_withdraw: document.getElementById("f_date_of_withdraw").value || null,
          pen_number: document.getElementById("f_pen_number").value || null,
          b_group: document.getElementById("f_b_group").value || null,
          state: document.getElementById("f_state").value || null,
          category: document.getElementById("f_category").value || null,
          religion: document.getElementById("f_religion").value || null,
          address: document.getElementById("f_address").value || null,

          // Contact
          father_phone: document.getElementById("f_father_phone").value || null,
          mother_phone: document.getElementById("f_mother_phone").value || null,
          aadhaar_number: document.getElementById("f_aadhaar").value || null,
          visible: document.getElementById("f_visible")?.value === "1",

          // Transport
          bus_service: document.getElementById("f_bus_service").value || "0",
          route_id: routeVal,

          // ✅ Bus gender
          bus_gender: document.getElementById("f_bus_gender")?.value || null,

          // Previous School
          prev_school_name: document.getElementById("f_prev_school_name")?.value || null,
          prev_school_address: document.getElementById("f_prev_school_address")?.value || null,
          prev_class: document.getElementById("f_prev_class")?.value || null,
          prev_admission_no: document.getElementById("f_prev_admission_no")?.value || null,
        };

        if (!payload.bus_gender) payload.bus_gender = null;

        // Siblings
        [1, 2, 3, 4].forEach((slot) => {
          const rawId = document.getElementById(`f_sibling_id_${slot}`)?.value ?? "";
          const rawName = document.getElementById(`f_sibling_name_${slot}`)?.value ?? "";

          const cleanedId = rawId ? normalizeSiblingToken(rawId) : null;
          const cleanedName = rawName ? String(rawName).trim() : null;

          payload[`sibling_id_${slot}`] = cleanedId || null;
          payload[`sibling_name_${slot}`] = cleanedName || null;
        });

        if (!payload.name) {
          Swal.showValidationMessage("Full name is required");
          return false;
        }
        if (!payload.class_id) {
          Swal.showValidationMessage("Class selection is required");
          return false;
        }
        if (!payload.section_id) {
          Swal.showValidationMessage("Section selection is required");
          return false;
        }
        if (admissionTypeList.length && !payload.admission_type_id) {
          Swal.showValidationMessage("Admission Type is required");
          return false;
        }

        const collectOverrideItems = (checkSelector, amountSelector) => {
          const items = [];
          document.querySelectorAll(`${checkSelector}:checked`).forEach((checkbox) => {
            const index = checkbox.dataset.index;
            const input = document.querySelector(`${amountSelector}[data-index="${index}"]`);
            const amount = Number(input?.value);
            const feeHeadingId = Number(input?.dataset.headId);
            items.push({
              fee_heading_id: feeHeadingId,
              amount,
              minimum_amount: Number(input?.min || 0),
              transportation_id: input?.dataset.transportationId || null,
            });
          });
          return items;
        };

        const feeOverrideItems = collectOverrideItems(
          ".fee-override-check",
          ".fee-override-amount"
        );
        const transportOverrideItems = collectOverrideItems(
          ".transport-override-check",
          ".transport-override-amount"
        );
        const collectRemovedOverrideIds = (checkSelector) =>
          Array.from(document.querySelectorAll(checkSelector))
            .filter((checkbox) => !checkbox.checked && checkbox.dataset.existingId)
            .map((checkbox) => Number(checkbox.dataset.existingId))
            .filter(Boolean);
        const removedFeeOverrideIds = isEdit
          ? collectRemovedOverrideIds(".fee-override-check")
          : [];
        const removedTransportOverrideIds = isEdit
          ? collectRemovedOverrideIds(".transport-override-check")
          : [];
        const allOverrideItems = [...feeOverrideItems, ...transportOverrideItems];

        if (
          allOverrideItems.some(
            (item) =>
              !item.fee_heading_id || !Number.isFinite(item.amount) || item.amount < 0
              || item.amount < item.minimum_amount
          )
        ) {
          Swal.showValidationMessage(
            "Override amounts must be valid and cannot be lower than Paid + Concession"
          );
          return false;
        }
        if (allOverrideItems.length && !payload.session_id) {
          Swal.showValidationMessage("Session is required when adding fee overrides");
          return false;
        }

        try {
          let savedStudent = s;
          if (isEdit) {
            await api.put(`/students/edit/${s.id}`, payload);
          } else {
            const { data } = await api.post("/students/add", payload);
            savedStudent = data;
          }

          const overrideWarnings = [];
          if (feeOverrideItems.length) {
            try {
              await api.post("/student-fee-structures/bulk-overrides", {
                student_id: Number(savedStudent.id),
                session_id: Number(payload.session_id),
                items: feeOverrideItems,
                remarks:
                  document.getElementById("f_fee_override_remarks")?.value.trim() ||
                  "Admission fee adjustment",
              });
            } catch (overrideError) {
              console.error("save admission fee overrides:", overrideError);
              overrideWarnings.push(
                overrideError.response?.data?.error || "Fee overrides could not be saved"
              );
            }
          }

          if (transportOverrideItems.length) {
            const transportRemarks =
              document.getElementById("f_transport_override_remarks")?.value.trim() ||
              "Admission transport fee override";
            for (const item of transportOverrideItems) {
              try {
                await api.post("/student-transport-fee-head-amounts/upsert", {
                  student_id: Number(savedStudent.id),
                  session_id: Number(payload.session_id),
                  fee_head_id: item.fee_heading_id,
                  transportation_id:
                    item.transportation_id || payload.route_id || null,
                  amount: item.amount,
                  remarks: transportRemarks,
                });
              } catch (overrideError) {
                console.error("save admission transport override:", overrideError);
                overrideWarnings.push(
                  overrideError.response?.data?.error ||
                    `Transport override for fee head ${item.fee_heading_id} could not be saved`
                );
              }
            }
          }
          for (const overrideId of removedFeeOverrideIds) {
            try {
              await api.delete(`/student-fee-structures/${overrideId}`);
            } catch (overrideError) {
              console.error("remove student fee override:", overrideError);
              overrideWarnings.push(
                overrideError.response?.data?.error ||
                  `Fee override ${overrideId} could not be removed`
              );
            }
          }
          for (const overrideId of removedTransportOverrideIds) {
            try {
              await api.delete(`/student-transport-fee-head-amounts/${overrideId}`);
            } catch (overrideError) {
              console.error("remove student transport override:", overrideError);
              overrideWarnings.push(
                overrideError.response?.data?.error ||
                  `Transport override ${overrideId} could not be removed`
              );
            }
          }
          return {
            payload,
            overrideWarnings,
            overrideCount:
              allOverrideItems.length +
              removedFeeOverrideIds.length +
              removedTransportOverrideIds.length,
          };
        } catch (err) {
          console.error("showStudentForm submit:", err);
          const message =
            err.response?.data?.error ||
            err.response?.data?.message ||
            "Failed to save student record";
          Swal.showValidationMessage(message);
          return false;
        }
      },
      didOpen: () => {
        const closeBtn = document.getElementById("studentSwalCloseBtn");
        if (closeBtn) closeBtn.addEventListener("click", () => Swal.close());

        /* ============================================================
         * ✅ Auto House on session change (add-mode only)
         * ============================================================ */
        const sessionSel = document.getElementById("f_session_id");
        const houseSel = document.getElementById("f_house_id");

        let houseManuallyChanged = false;

        if (houseSel) {
          houseSel.addEventListener("change", () => {
            houseManuallyChanged = true;
          });
        }

        const autoPickHouse = () => {
          if (!houseSel) return;
          if (isEdit) return;
          if (houseManuallyChanged) return;

          const sessionId = sessionSel?.value || "";
          const best = getLowestStrengthHouseId(sessionId);
          if (best) houseSel.value = best;
        };

        if (sessionSel) {
          sessionSel.addEventListener("change", () => {
            autoPickHouse();
          });
        }

        autoPickHouse();

        /* ============================================================
         * ✅ Session-wise Transport Route reload
         * ============================================================ */
        const routeSel = document.getElementById("f_route_id");

        const buildRouteOptionsHtml = (list, selectedId) => {
          const safe = Array.isArray(list) ? list : [];
          const opts = [`<option value="">No Transport</option>`];

          safe.forEach((t) => {
            const label = String(t?.Villages || "").replace(/"/g, "&quot;");
            const cost = t?.Cost ?? "";
            const id = t?.id;

            opts.push(
              `<option value="${id}" data-cost="${Number(cost || 0)}" ${String(id) === String(selectedId) ? "selected" : ""}>
                ${label}${cost ? ` — ₹${cost}` : ""}
              </option>`
            );
          });

          return opts.join("");
        };

        const fetchTransportForSessionId = async (sessionId) => {
          if (!routeSel) return;

          const prevSelected = routeSel.value || "";

          try {
            routeSel.innerHTML = `<option value="">Loading...</option>`;

            const ssObj = sessions.find((x) => String(x.id) === String(sessionId));
            const sessionName = ssObj?.name ? String(ssObj.name) : "";

            if (sessionName) {
              const resp = await api.get(`/transportations?session=${encodeURIComponent(sessionName)}`);
              const list = Array.isArray(resp.data) ? resp.data : [];
              routeSel.innerHTML = buildRouteOptionsHtml(list, prevSelected);
            } else {
              const resp = await api.get(`/transportations/active`);
              const list = Array.isArray(resp?.data?.data) ? resp.data.data : [];
              routeSel.innerHTML = buildRouteOptionsHtml(list, prevSelected);
            }
          } catch (e) {
            console.error("fetchTransportForSessionId:", e);
            routeSel.innerHTML = `<option value="">No Transport</option>`;
          }
        };

        if (sessionSel) {
          sessionSel.addEventListener("change", () => {
            fetchTransportForSessionId(sessionSel.value || "");
          });
        }

        fetchTransportForSessionId(sessionSel?.value || "");

        /* ============================================================
         * Tabs wire-up
         * ============================================================ */
        const btns = document.querySelectorAll(".tabbtn");
        const panes = {
          mandatory: document.getElementById("pane-mandatory"),
          personal: document.getElementById("pane-personal"),
          contact: document.getElementById("pane-contact"),
          transport: document.getElementById("pane-transport"),
          transportoverride: document.getElementById("pane-transportoverride"),
          feeoverride: document.getElementById("pane-feeoverride"),
          siblings: document.getElementById("pane-siblings"),
          prevschool: document.getElementById("pane-prevschool"),
        };
        btns.forEach((b) =>
          b.addEventListener("click", () => {
            btns.forEach((x) => x.classList.remove("active"));
            b.classList.add("active");
            Object.values(panes).filter(Boolean).forEach((p) => p.classList.remove("active"));
            const t = b.getAttribute("data-tab");
            if (panes[t]) panes[t].classList.add("active");
          })
        );

        const wireOverrideAmounts = (checkSelector, amountSelector) => {
          document.querySelectorAll(checkSelector).forEach((checkbox) => {
            checkbox.addEventListener("change", () => {
              const input = document.querySelector(
                `${amountSelector}[data-index="${checkbox.dataset.index}"]`
              );
              if (input) input.disabled = !checkbox.checked;
            });
          });
        };
        wireOverrideAmounts(".fee-override-check", ".fee-override-amount");
        wireOverrideAmounts(".transport-override-check", ".transport-override-amount");

        const refreshAddTransportOverrideRows = async () => {
          if (isEdit || !canAddTransportOverrides) return;
          const tbody = document.getElementById("transportOverrideRows");
          const classId = document.getElementById("f_class_id")?.value || "";
          const sessionId = document.getElementById("f_session_id")?.value || "";
          if (!tbody) return;
          if (!classId || !sessionId) {
            tbody.innerHTML = `<tr><td colspan="5" style="text-align:center;color:#6b7280">Select class and session to load transport-applicable fee heads.</td></tr>`;
            return;
          }

          tbody.innerHTML = `<tr><td colspan="5" style="text-align:center;color:#6b7280">Loading transport-applicable fee heads...</td></tr>`;
          try {
            const { data } = await api.get(`/fee-structures/class/${classId}`, {
              params: { session_id: sessionId, isActive: true },
            });
            const rows = Array.isArray(data) ? data : data?.fees || data?.data || [];
            const applicableByHead = new Map();
            rows
              .filter((row) => isTransportApplicable(row.transportApplicable))
              .forEach((row) => {
                if (!applicableByHead.has(String(row.fee_heading_id))) {
                  applicableByHead.set(String(row.fee_heading_id), row);
                }
              });
            const applicable = [...applicableByHead.values()];
            const normalTransport = Number(
              document.getElementById("f_route_id")?.selectedOptions?.[0]?.dataset?.cost || 0
            );

            tbody.innerHTML = applicable.length
              ? applicable
                  .map(
                    (row, index) => `<tr>
                      <td><input type="checkbox" class="transport-override-check" data-index="add-${index}" /></td>
                      <td>${escapeHtmlAttr(row.FeeHeading?.fee_heading || row.fee_heading || `Fee Head ${row.fee_heading_id}`)}</td>
                      <td>₹${normalTransport.toFixed(2)}</td>
                      <td>₹0.00</td>
                      <td><input type="number" min="0" step="0.01" class="form-field amount-input transport-override-amount" data-index="add-${index}" data-head-id="${row.fee_heading_id}" value="0" disabled /></td>
                    </tr>`
                  )
                  .join("")
              : `<tr><td colspan="5" style="text-align:center;color:#6b7280">No transport-applicable fee heads were found for the selected class and session.</td></tr>`;
            wireOverrideAmounts(".transport-override-check", ".transport-override-amount");
          } catch (err) {
            console.error("load transport-applicable admission fee heads:", err);
            tbody.innerHTML = `<tr><td colspan="5" style="text-align:center;color:#b91c1c">Transport-applicable fee heads could not be loaded.</td></tr>`;
          }
        };

        if (!isEdit && canAddTransportOverrides) {
          document.getElementById("f_class_id")?.addEventListener(
            "change",
            refreshAddTransportOverrideRows
          );
          document.getElementById("f_session_id")?.addEventListener(
            "change",
            refreshAddTransportOverrideRows
          );
          document.getElementById("f_route_id")?.addEventListener(
            "change",
            refreshAddTransportOverrideRows
          );
          refreshAddTransportOverrideRows();
        }

        /* ============================================================
         * Sibling pickers
         * ============================================================ */
        [1, 2, 3, 4].forEach((slot) => {
          const clsSel = document.getElementById(`sib_class_${slot}`);
          const secSel = document.getElementById(`sib_section_${slot}`);
          const stuSel = document.getElementById(`sib_student_${slot}`);
          const hiddenId = document.getElementById(`f_sibling_id_${slot}`);
          const hiddenName = document.getElementById(`f_sibling_name_${slot}`);

          if (!clsSel || !secSel || !stuSel) return;

          const populateSectionsForClass = async (classId, selectedSection = "") => {
            let available = sectionOptionsAll;
            if (classId) {
              try {
                const { data } = await api.get("/sections", { params: { class_id: classId } });
                const rows = Array.isArray(data) ? data : data?.data;
                if (Array.isArray(rows)) {
                  available = rows.map((sec) => ({ id: sec.id, name: sec.section_name }));
                }
              } catch (err) {
                console.error("load sibling sections:", err);
              }
            }
            // Keep an existing section visible even if its mapping was removed.
            const selected = sectionOptionsAll.find((sec) => String(sec.id) === String(selectedSection));
            if (selected && !available.some((sec) => String(sec.id) === String(selectedSection))) {
              available = [...available, selected];
            }
            secSel.innerHTML = `<option value="">All Sections</option>` + available
              .map((sec) => `<option value="${sec.id}">${escapeHtmlAttr(sec.name)}</option>`).join("");
            secSel.value = String(selectedSection || "");
          };

          const fetchAndPopulateStudents = async (classId, sectionId) => {
            stuSel.innerHTML = `<option value="">Loading...</option>`;

            if (!classId) {
              stuSel.innerHTML = `<option value="">Select class first</option>`;
              return;
            }

            try {
              const url = `/students/sibling-list?class_id=${classId}${
                sectionId ? `&section_id=${sectionId}` : ""
              }`;
              const { data } = await api.get(url);

              if (!Array.isArray(data) || data.length === 0) {
                stuSel.innerHTML = `<option value="">No students found</option>`;
                return;
              }

              const opts = [`<option value="">Select Student</option>`].concat(
                data
                  .filter((st) => String(st.admission_number || "").trim() !== "")
                  .map((st) => {
                    const token = String(st.admission_number).trim();
                    return `<option value="${token}"
                              data-name="${(st.name || "").replace(/"/g, "&quot;")}"
                              data-pk="${st.id}"
                              data-an="${token}">
                              ${st.name} (AN:${token})
                            </option>`;
                  })
              );

              stuSel.innerHTML = opts.join("");
            } catch (err) {
              console.error("fetchAndPopulateStudents:", err);
              stuSel.innerHTML = `<option value="">Error loading</option>`;
            }
          };

          clsSel.onchange = async () => {
            const classId = clsSel.value;
            hiddenId.value = "";
            hiddenName.value = "";
            await populateSectionsForClass(classId);
            await fetchAndPopulateStudents(classId, secSel.value || "");
          };

          secSel.onchange = async () => {
            const classId = clsSel.value;
            const sectionId = secSel.value;
            hiddenId.value = "";
            hiddenName.value = "";
            await fetchAndPopulateStudents(classId, sectionId || "");
          };

          stuSel.onchange = () => {
            const opt = stuSel.selectedOptions[0];
            if (opt && opt.value) {
              hiddenId.value = opt.value;
              hiddenName.value = opt.dataset.name || opt.textContent || "";
            } else {
              hiddenId.value = "";
              hiddenName.value = "";
            }
          };

          let preStudentId = hiddenId.value || "";
          const preStudentName = hiddenName.value || "";
          let preClass = clsSel.value || "";
          let preSection = secSel.value || "";

          // derive missing class/section from already loaded students list
          // Admission-number-first prevents numeric admission numbers from matching wrong DB ids.
          if (preStudentId && (!preClass || !preSection)) {
            const linkedStudent = findStudentBySiblingToken(students, preStudentId);

            if (linkedStudent) {
              preClass = preClass || String(linkedStudent.class_id ?? "");
              preSection = preSection || String(linkedStudent.section_id ?? "");
            }
          }

          (async () => {
            if (preStudentId) {
              try {
                const { data } = await api.get("/students/sibling-list", {
                  params: { sibling_token: normalizeSiblingToken(preStudentId) },
                });
                const linked = Array.isArray(data) ? data[0] : null;
                if (linked) {
                  preClass = String(linked.class_id ?? "");
                  preSection = String(linked.section_id ?? "");
                  preStudentId = normalizeSiblingToken(linked.admission_number || preStudentId);
                  hiddenId.value = preStudentId;
                  hiddenName.value = linked.name || preStudentName;
                }
              } catch (err) {
                console.error("resolve saved sibling:", err);
              }
            }
            // Only load a class roster when the returned ID exists in the class master.
            const hasSavedClass = Array.from(clsSel.options).some(
              (option) => option.value !== "" && option.value === preClass
            );
            if (hasSavedClass) {
              clsSel.value = String(preClass);
              await populateSectionsForClass(preClass, preSection);

              await fetchAndPopulateStudents(preClass, preSection || "");

              if (preStudentId) {
                const normalizedPreStudentId = normalizeSiblingToken(preStudentId);
                const opt =
                  Array.from(stuSel.options).find(
                    (o) => String(o.dataset.an || "").trim() === normalizedPreStudentId
                  ) ||
                  Array.from(stuSel.options).find(
                    (o) => normalizeSiblingToken(o.value) === normalizedPreStudentId
                  );

                if (opt) {
                  opt.selected = true;
                  hiddenId.value = normalizeSiblingToken(opt.value);
                  hiddenName.value = opt.dataset.name || preStudentName || opt.textContent || "";
                }
              }
              return;
            }

            // case 2: class unknown but sibling already exists -> keep selected option visible
            if (preStudentId) {
              await populateSectionsForClass("", preSection);
              stuSel.innerHTML = `
                <option value="">Select Student</option>
                <option value="${String(preStudentId).replace(/"/g, "&quot;")}" selected>
                  ${(preStudentName || siblingDisplayToken(preStudentId)).replace(/</g, "&lt;")}
                </option>
              `;
              hiddenId.value = preStudentId;
              hiddenName.value = preStudentName;
              return;
            }

            // case 3: no existing sibling
            await populateSectionsForClass("");
            stuSel.innerHTML = `<option value="">Select class first</option>`;
          })();
        });
      },
    });

    if (!popup.isConfirmed) return;
    const overrideWarnings = popup.value?.overrideWarnings || [];
    const overrideCount = popup.value?.overrideCount || 0;
    Swal.fire(
      overrideWarnings.length ? (isEdit ? "Student Updated" : "Student Added") : "Success",
      overrideWarnings.length
        ? `The student was ${isEdit ? "updated" : "added"}, but: ${overrideWarnings.join("; ")}`
        : isEdit
          ? overrideCount
            ? "Student record and selected overrides updated successfully"
            : "Student record updated successfully"
          : overrideCount
            ? "New student and selected overrides added successfully"
            : "New student added successfully",
      overrideWarnings.length ? "warning" : "success"
    );
    fetchStudents();
  };

  const handleAdd = () => {
    if (!canAddStudents) return;
    showStudentForm("add", null);
  };
  const handleEdit = (student) => {
    if (!canEditStudents) return;
    showStudentForm("edit", student);
  };

  const handleViewAttendance = (student) => {
    const admission = String(student?.admission_number || "").trim();
    if (!admission) {
      Swal.fire("Missing Admission Number", "Attendance can be opened after admission number is available.", "warning");
      return;
    }

    navigate(`/student-attendance?admission=${encodeURIComponent(admission)}`);
  };

  const handleTcOperations = (student) => {
    const admission = String(student?.admission_number || "").trim();
    if (!admission) {
      Swal.fire("Missing Admission Number", "TC operations require an admission number.", "warning");
      return;
    }

    navigate(`/transfer-certificates?admission=${encodeURIComponent(admission)}`);
  };

  // ✅ NEW: click student row to open EDIT popup
  const handleRowClickOpenEdit = (stu) => {
    if (!canEditStudents) return;
    showStudentForm("edit", stu);
  };

  const handleExport = async () => {
  if (!canExportStudents) return;
  const html = `
    <style>
      .export-columns-wrap {
        text-align: left;
        max-height: 380px;
        overflow-y: auto;
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        padding: 12px;
        background: #fff;
      }
      .export-col-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px 16px;
      }
      .export-col-item {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
      }
      .export-top-actions {
        display: flex;
        gap: 8px;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;
        flex-wrap: wrap;
      }
      .export-top-actions-left,
      .export-top-actions-right {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
      }
      .export-mini-btn {
        border: 1px solid #d1d5db;
        background: #fff;
        border-radius: 8px;
        padding: 5px 10px;
        font-size: 12px;
        cursor: pointer;
      }
      .export-mini-btn:hover {
        background: #f9fafb;
      }
      .export-extra-options {
        text-align: left;
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        padding: 10px 12px;
        background: #f9fafb;
        margin-bottom: 10px;
      }
      .export-extra-option {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        color: #111827;
      }
      .export-extra-hint {
        margin-top: 6px;
        font-size: 11px;
        color: #6b7280;
      }
      .export-note {
        text-align: left;
        font-size: 12px;
        color: #6b7280;
        margin: 8px 0 0;
      }
      @media (max-width: 640px) {
        .export-col-grid {
          grid-template-columns: 1fr;
        }
      }
    </style>

    <div class="export-top-actions">
      <div class="export-top-actions-left">
        <button type="button" class="export-mini-btn" id="selectAllExportCols">Select All</button>
        <button type="button" class="export-mini-btn" id="defaultExportCols">Default</button>
        <button type="button" class="export-mini-btn" id="clearExportCols">Clear</button>
      </div>
      <div class="export-top-actions-right">
        <button type="button" class="export-mini-btn" id="downloadPdfExportCols">Download PDF</button>
      </div>
    </div>

    <div class="export-extra-options">
      <label class="export-extra-option">
        <input type="checkbox" id="includeSiblingDetailsExport" />
        <span>Include sibling details</span>
      </label>
      <div class="export-extra-hint">
        This will automatically add sibling columns in both Excel and PDF export.
      </div>
    </div>

    <div class="export-columns-wrap">
      <div class="export-col-grid">
        ${EXPORT_COLUMN_OPTIONS.map(
          (col) => `
            <label class="export-col-item">
              <input
                type="checkbox"
                class="export-col-checkbox"
                value="${col.key}"
                ${DEFAULT_EXPORT_COLUMNS.includes(col.key) ? "checked" : ""}
              />
              <span>${col.label}</span>
            </label>
          `
        ).join("")}
      </div>
    </div>

    <div class="export-note">
      Excel is best for editing/filtering. PDF is best for printing/sharing.
    </div>
  `;

  const collectSelectedColumns = () =>
    Array.from(document.querySelectorAll(".export-col-checkbox:checked")).map((el) => el.value);

  const getIncludeSiblings = () =>
    Boolean(document.getElementById("includeSiblingDetailsExport")?.checked);

  const downloadExportFile = async (format, selectedColumns, includeSiblings) => {
    const endpoint = format === "pdf" ? "/students/export-pdf" : "/students/export-excel";
    const extension = format === "pdf" ? "pdf" : "xlsx";
    const mimeFallback =
      format === "pdf"
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    const resp = await api.post(
      endpoint,
      {
        columns: selectedColumns,
        class_id: selectedClass || null,
        session_id: selectedSessionFilter || null,
        status: selectedStatus || null,
        search: search || "",
        include_siblings_details: includeSiblings,
      },
      { responseType: "blob" }
    );

    const blob = new Blob([resp.data], {
      type: resp.headers["content-type"] || mimeFallback,
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Students_Export_${new Date().toISOString().split("T")[0]}.${extension}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const result = await Swal.fire({
    title: "Select Columns to Export",
    html,
    width: 760,
    showCancelButton: true,
    showDenyButton: true,
    confirmButtonText: "Download Excel",
    denyButtonText: "Download PDF",
    cancelButtonText: "Cancel",
    focusConfirm: false,
    didOpen: () => {
      const getChecks = () => Array.from(document.querySelectorAll(".export-col-checkbox"));

      document.getElementById("selectAllExportCols")?.addEventListener("click", () => {
        getChecks().forEach((el) => {
          el.checked = true;
        });
      });

      document.getElementById("defaultExportCols")?.addEventListener("click", () => {
        getChecks().forEach((el) => {
          el.checked = DEFAULT_EXPORT_COLUMNS.includes(el.value);
        });
      });

      document.getElementById("clearExportCols")?.addEventListener("click", () => {
        getChecks().forEach((el) => {
          el.checked = false;
        });
      });

      document.getElementById("downloadPdfExportCols")?.addEventListener("click", async () => {
        const selectedColumns = collectSelectedColumns();
        const includeSiblings = getIncludeSiblings();

        if (!selectedColumns.length) {
          Swal.showValidationMessage("Please select at least one column");
          return;
        }

        try {
          Swal.showLoading();
          await downloadExportFile("pdf", selectedColumns, includeSiblings);
          Swal.close();
          await Swal.fire("Exported", "Student PDF downloaded successfully", "success");
        } catch (err) {
          console.error("handleExport PDF quick action:", err);
          Swal.showValidationMessage(
            err.response?.data?.message || "Failed to export student PDF"
          );
        }
      });
    },
    preConfirm: () => {
      const selectedColumns = collectSelectedColumns();
      const includeSiblings = getIncludeSiblings();

      if (!selectedColumns.length) {
        Swal.showValidationMessage("Please select at least one column");
        return false;
      }

      return { selectedColumns, includeSiblings };
    },
    preDeny: () => {
      const selectedColumns = collectSelectedColumns();
      const includeSiblings = getIncludeSiblings();

      if (!selectedColumns.length) {
        Swal.showValidationMessage("Please select at least one column");
        return false;
      }

      return { selectedColumns, includeSiblings };
    },
  });

  if (!result.isConfirmed && !result.isDenied) return;

  try {
    const { selectedColumns, includeSiblings } = result.value || {};
    const format = result.isDenied ? "pdf" : "excel";

    await downloadExportFile(format, selectedColumns, includeSiblings);

    Swal.fire(
      "Exported",
      format === "pdf"
        ? "Student PDF downloaded successfully"
        : "Student Excel downloaded successfully",
      "success"
    );
  } catch (err) {
    console.error("handleExport:", err);
    Swal.fire(
      "Error",
      err.response?.data?.message ||
        (result.isDenied ? "Failed to export student PDF" : "Failed to export student data"),
      "error"
    );
  }
};

  const selectImportFile = (files) => {
    if (importing || !files?.length) return;
    setImportStatus("");
    setImportProgress(0);
    if (files.length !== 1 || !/\.xlsx?$/i.test(files[0].name)) {
      setImportFile(null);
      setImportError("Please select one Excel file (.xlsx or .xls).");
      return;
    }
    setImportFile(files[0]);
    setImportError("");
  };

  const handleImport = async () => {
    if (!canImportStudents || !importFile || importing) return;
    setImporting(true);
    setImportProgress(0);
    setImportStatus("uploading");
    setImportError("");
    const fd = new FormData();
    fd.append("file", importFile);

    try {
      const res = await api.post("/students/import-students", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: ({ loaded, total }) => {
          if (!total) return;
          setImportProgress(Math.min(100, Math.floor((loaded / total) * 100)));
          if (loaded >= total) setImportStatus("processing");
        },
      });
      const data = res.data || {};
      const invalid = Array.isArray(data.invalid) ? data.invalid : [];
      const duplicates = data.summary?.duplicates ?? data.duplicates?.length ?? 0;
      const invalidCount = data.summary?.invalid ?? invalid.length;
      const inserted = data.summary?.inserted;
      const hasSkippedRows = invalidCount > 0 || duplicates > 0;
      const reasons = new Map();
      invalid.forEach((row) => {
        const reason = row.error || "Invalid row";
        reasons.set(reason, (reasons.get(reason) || 0) + 1);
      });
      const details = [
        data.message || "Import completed",
        `Skipped: ${invalidCount} invalid rows, ${duplicates} duplicates.`,
        ...Array.from(reasons, ([reason, count]) => `${reason}: ${count} row(s)`),
      ].join("\n");
      setImportProgress(100);
      setImportStatus(inserted === 0 ? "error" : "complete");
      if (inserted === 0 || hasSkippedRows) setImportError(details);
      if (inserted !== 0) setImportFile(null);
      Swal.fire(
        inserted === 0 ? "No students imported" : hasSkippedRows ? "Import completed with skipped rows" : "Imported",
        details,
        inserted === 0 || hasSkippedRows ? "warning" : "success"
      );
      fetchStudents();
    } catch (err) {
      console.error("handleImport:", err);
      setImportStatus("error");
      setImportError(err.response?.data?.message || "Failed to import data. Please try again.");
    } finally {
      setImporting(false);
    }
  };

  const studentHasSibling = (stu) => {
    return (
      !!(stu.sibling_id_1 || stu.sibling_id_2 || stu.sibling_id_3 || stu.sibling_id_4) ||
      !!(stu.sibling_name_1 || stu.sibling_name_2 || stu.sibling_name_3 || stu.sibling_name_4)
    );
  };

  const normalizeSearchText = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[^a-z0-9]/g, "");

  const compareAdmissionNumberAsc = (a, b) => {
    const aVal = String(a?.admission_number || "").trim();
    const bVal = String(b?.admission_number || "").trim();

    const aNum = /^\d+$/.test(aVal) ? Number(aVal) : null;
    const bNum = /^\d+$/.test(bVal) ? Number(bVal) : null;

    if (aNum !== null && bNum !== null) return aNum - bNum;
    if (aNum !== null) return -1;
    if (bNum !== null) return 1;

    return aVal.localeCompare(bVal, undefined, {
      numeric: true,
      sensitivity: "base",
    });
  };

  const studentsMatchingFilters = useMemo(() => {
    const q = normalizeSearchText(search);

    return students
      .filter((stu) => {
        const searchableFields = [
          stu.admission_number,
          stu.name,
          stu.father_name,
          stu.mother_name,
          stu.aadhaar_number,
          stu.class_name,
          stu.section_name,
          stu.roll_number,
          getAdmissionTypeLabel(stu, admissionTypes),
        ];

        const textMatch =
          !q || searchableFields.some((v) => normalizeSearchText(v).includes(q));

        const classMatch = !selectedClass || String(stu.class_id) === String(selectedClass);
        const sectionMatch =
          !selectedSection || String(stu.section_id) === String(selectedSection);
        const hasSibling = studentHasSibling(stu);
        let siblingMatch = true;
        if (hasSiblingFilter === "has") siblingMatch = hasSibling;
        if (hasSiblingFilter === "no") siblingMatch = !hasSibling;

        return textMatch && classMatch && sectionMatch && siblingMatch;
      })
      .slice()
      .sort(compareAdmissionNumberAsc);
  }, [
    students,
    search,
    selectedClass,
    selectedSection,
    hasSiblingFilter,
    admissionTypes,
  ]);

  const filteredStudents = useMemo(
    () => studentsMatchingFilters.filter((stu) => stu.status === selectedStatus),
    [studentsMatchingFilters, selectedStatus]
  );

  const enabledCount = studentsMatchingFilters.filter((s) => s.status === "enabled").length;
  const disabledCount = studentsMatchingFilters.filter((s) => s.status === "disabled").length;
  const totalCount = enabledCount + disabledCount;

  const handleSiblingClick = async (token) => {
    if (!token) return;

    const raw = normalizeSiblingToken(token);
    if (!raw) return;

    const tryExactAN = async () => {
      try {
        const resp = await api.get(`/students/admission/${encodeURIComponent(raw)}`);
        return Array.isArray(resp.data) ? resp.data[0] || null : resp.data || null;
      } catch {
        return null;
      }
    };

    const tryListANExact = async () => {
      try {
        const resp = await api.get(`/students?admission_number=${encodeURIComponent(raw)}`);
        const arr = Array.isArray(resp.data) ? resp.data : [];
        return arr.find((s) => String(s.admission_number || "").trim() === raw) || null;
      } catch {
        return null;
      }
    };

    let respData = await tryExactAN();
    if (!respData) respData = await tryListANExact();

    if (!respData) {
      Swal.fire("Not Found", "Sibling record not available.", "warning");
      return;
    }

    if (canEditStudents) {
      showStudentForm("edit", respData);
    } else {
      handleView(respData);
    }
  };

  const PhotoCell = ({ student }) => {
    const src = buildPhotoURL(student.photo);
    const hasPhoto = !!student.photo;

    return (
      <div className="d-flex align-items-center gap-2">
        <div className="position-relative">
          <img
            src={hasPhoto ? src : NO_PHOTO_SVG}
            alt={`${student.name || "Student"} photo`}
            className="rounded-circle"
            style={{
              width: 42,
              height: 42,
              objectFit: "cover",
              border: "2px solid var(--edb-border)",
            }}
            onError={(e) => {
              e.currentTarget.src = NO_PHOTO_SVG;
            }}
          />
        </div>
        {canUploadStudentPhoto && (
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={(ev) => {
              ev.stopPropagation();
              promptAndUploadPhoto(student);
            }}
            title={hasPhoto ? "Replace photo" : "Upload photo"}
          >
            <i className="bi bi-camera" style={{ fontSize: "0.875rem" }}></i>
          </button>
        )}
      </div>
    );
  };

  // VIEW POPUP (kept as-is for sibling flow / optional future use)
  function handleView(student) {
    const siblingRows = [];
    for (let i = 1; i <= 4; i++) {
      const id = student[`sibling_id_${i}`];
      const name = student[`sibling_name_${i}`];
      if (id || name) {
        siblingRows.push({
          label: `Sibling ${i}`,
          value: name ? name : siblingDisplayToken(id),
          token: id,
        });
      }
    }

    const statusBadge =
      student.status === "enabled"
        ? '<span class="badge bg-success">ENABLED</span>'
        : '<span class="badge bg-secondary">DISABLED</span>';

    const viewBloodGroup =
      student.b_group ||
      student.B_group ||
      student.B_GROUP ||
      student.blood_group ||
      student.Blood_Group ||
      "-";

    const viewState = student.state || student.State || student.STATE || "-";
    const viewGender = student.gender || student.Gender || student.GENDER || "-";
    const viewBusGender =
      student.bus_gender || student.busGender || student.BusGender || student.BUS_GENDER || "-";

    const fields = [
      { label: "Admission Number", value: student.admission_number || "-" },
      { label: "Full Name", value: student.name || "-" },
      { label: "Gender", value: viewGender },
      { label: "Bus Gender", value: viewBusGender },
      { label: "Father's Name", value: student.father_name || "-" },
      { label: "Mother's Name", value: student.mother_name || "-" },
      { label: "Class", value: student.class_name || "-" },
      { label: "Section", value: student.section_name || "-" },
      { label: "House", value: student.house_name || "-" },
      { label: "Session", value: student.session_name || "-" },
      { label: "Roll Number", value: student.roll_number || "-" },
      { label: "Date of Birth", value: student.Date_Of_Birth || "-" },
      { label: "Admission Date", value: student.date_of_admission || "-" },
      { label: "Withdrawal Date", value: student.date_of_withdraw || "-" },
      { label: "PEN Number", value: student.pen_number || "-" },
      { label: "Blood Group", value: viewBloodGroup },
      { label: "State", value: viewState },
      { label: "Category", value: student.category || "-" },
      { label: "Religion", value: student.religion || "-" },
      { label: "Bus Service Fee", value: `₹${student.bus_service || 0}` },
      { label: "Public Visibility", value: student.visible ? "Yes" : "No" },
      { label: "Father's Phone", value: student.father_phone || "-" },
      { label: "Mother's Phone", value: student.mother_phone || "-" },
      {
        label: "Aadhaar Number",
        value: student.aadhaar_number
          ? student.aadhaar_number.replace(/(\d{4})(\d{4})(\d{4})/, "$1-$2-$3")
          : "-",
      },
      { label: "Admission Type", value: getAdmissionTypeLabel(student, admissionTypes) || "-" },
    ];

    if (canManageStudents) {
      fields.push({
        label: "Concession",
        value: student.concession_name || "-",
      });
    }

    fields.push({ label: "Transport Route", value: formatTransportById(student.route_id) });
    fields.push({ label: "Status", value: statusBadge });
    fields.push({ label: "Residential Address", value: student.address || "-", full: true });
    fields.push({ label: "Previous School", value: student.prev_school_name || "-" });
    fields.push({ label: "Previous Class", value: student.prev_class || "-" });
    fields.push({ label: "Previous Adm. #", value: student.prev_admission_no || "-" });

    if (student.prev_school_address) {
      fields.push({ label: "Previous School Address", value: student.prev_school_address, full: true });
    }

    const photoUrl = buildPhotoURL(student.photo);
    const hasPhoto = !!student.photo;

    const fieldHtml = fields
      .map(
        (f) => `
      <div class="detail-item ${f.full ? "full-row" : ""}">
        <div class="detail-label">${f.label}</div>
        <div class="detail-value">${f.value}</div>
      </div>
    `
      )
      .join("");

    const siblingHtml = siblingRows.length
      ? siblingRows
          .map(
            (s) => `
      <div class="detail-item sibling-item" data-sibling-token="${s.token || ""}">
        <div class="detail-label">${s.label}</div>
        <div class="detail-value" style="cursor: ${
          s.token ? "pointer" : "default"
        }; color: ${s.token ? "#0d6efd" : "#6b7280"}; font-weight: 500;" ${
              s.token ? `data-sibling-token="${s.token}"` : ""
            }>${s.value}</div>
      </div>
    `
          )
          .join("")
      : `
      <div class="detail-item">
        <div class="detail-label">Siblings</div>
        <div class="detail-value">None recorded</div>
      </div>
    `;

    const photoHtml = `
      <div class="d-flex align-items-center justify-content-center gap-3 mb-3 p-3 bg-light rounded">
        <img src="${hasPhoto ? photoUrl : NO_PHOTO_SVG}" alt="Student Photo"
          class="rounded-circle border shadow-sm"
          style="width: 90px; height: 90px; object-fit: cover;" />
        ${
          canUploadStudentPhoto
            ? `<button id="btnChangePhoto" class="btn btn-outline-primary btn-sm">${
                hasPhoto ? "Change Photo" : "Upload Photo"
              }</button>`
            : ""
        }
        ${
          canPrintAdmissionForm
            ? `<button id="btnPrintAdmissionForm" class="btn btn-outline-success btn-sm">Print Form</button>`
            : ""
        }
      </div>
    `;

    const html = `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
        <div class="d-flex align-items-start justify-content-between gap-2 mb-2">
          <div>
            <h3 class="mb-0 fw-bold text-primary" style="font-size: 1.05rem;">${student.name || "Student"} — Profile</h3>
            <div class="text-muted" style="font-size: 0.8rem;">Admission # ${student.admission_number || "-"}</div>
          </div>
        </div>
        ${photoHtml}
        <div class="row g-3">
          <div class="col-md-6">
            ${fieldHtml
              .split("</div>")
              .slice(0, Math.ceil(fields.length / 2))
              .join("</div>")}
          </div>
          <div class="col-md-6">
            ${siblingHtml}
          </div>
        </div>
      </div>
      <style>
        .detail-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 10px 0;
          border-bottom: 1px solid var(--edb-border);
        }
        .detail-item:last-child { border-bottom: none; }
        .detail-label {
          font-weight: 700;
          color: var(--edb-muted-text);
          flex: 0 0 140px;
          font-size: 0.85rem;
        }
        .detail-value {
          color: var(--edb-text);
          flex: 1;
          word-break: break-word;
          font-size: 0.9rem;
        }
        .sibling-item .detail-value:hover { text-decoration: underline; }
        @media (max-width: 768px) {
          .detail-item { flex-direction: column; gap: 4px; }
          .detail-label { flex: 0 0 auto; }
        }
      </style>
    `;

    Swal.fire({
      title: "",
      html,
      width: 900,
      showCloseButton: true,
      showConfirmButton: false,
      customClass: {
        popup: "border-0 shadow-lg",
        content: "p-0",
      },
      didOpen: () => {
        const popup = Swal.getPopup();
        if (popup) {
          const siblingEls = popup.querySelectorAll("[data-sibling-token]");
          siblingEls.forEach((el) => {
            const token = el.getAttribute("data-sibling-token");
            if (token) {
              el.addEventListener("click", (e) => {
                e.stopPropagation();
                Swal.close();
                setTimeout(() => handleSiblingClick(token), 100);
              });
            }
          });
        }
        const btn = document.getElementById("btnChangePhoto");
        if (btn && canUploadStudentPhoto) {
          btn.addEventListener("click", (e) => {
            e.stopPropagation();
            Swal.close();
            promptAndUploadPhoto(student);
          });
        }

        const printBtn = document.getElementById("btnPrintAdmissionForm");
        if (printBtn && canPrintAdmissionForm) {
          printBtn.addEventListener("click", (e) => {
            e.stopPropagation();
            Swal.close();
            setTimeout(() => handlePrintAdmissionForm(student), 100);
          });
        }
      },
    });
  }

  // ✅ PART-2: UI
  return (
    <div
      className="container-fluid py-3 students-page"
      style={{
        background:
          "radial-gradient(circle at top left, color-mix(in srgb, var(--edb-primary) 14%, transparent), transparent 32%), linear-gradient(180deg, var(--edb-surface) 0%, var(--edb-surface) 260px, var(--edb-surface) 100%)",
        minHeight: "100vh",
      }}
    >
      {/* ✅ Professional compact header */}
      <div
        className="students-topbar d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 p-3"
        style={{
          background: "color-mix(in srgb, var(--edb-surface) 92%, transparent)",
          border: "1px solid color-mix(in srgb, var(--edb-border) 95%, transparent)",
          borderRadius: 18,
          boxShadow: "0 14px 32px color-mix(in srgb, var(--edb-primary-dark) 7%, transparent)",
          backdropFilter: "blur(10px)",
        }}
      >
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <span
              className="d-inline-flex align-items-center justify-content-center"
              style={{
                width: 34,
                height: 34,
                borderRadius: 12,
                background: "linear-gradient(135deg,var(--edb-primary),#06b6d4)",
                color: "var(--edb-on-primary)",
              }}
            >
              <i className="bi bi-mortarboard-fill"></i>
            </span>
            <h2 className="h5 mb-0 fw-bold text-dark">Student Management</h2>
          </div>
          <p className="mb-0 text-muted" style={{ fontSize: "0.83rem" }}>
            Manage admissions, siblings, transport, houses, exports, and student records in one place.
          </p>
        </div>

        <div className="d-flex gap-2 flex-wrap align-items-center justify-content-end">
          {/* ✅ Columns Toggle (Compact / Full) */}
          <button
            type="button"
            className={`btn btn-sm rounded-pill px-3 ${showAllColumns ? "btn-outline-primary" : "btn-primary"}`}
            onClick={toggleColumnsMode}
            title="Toggle Compact / Full columns"
          >
            <i className="bi bi-layout-text-sidebar-reverse me-1"></i>
            {showAllColumns ? "Full" : "Compact"}
          </button>

          {canAddStudents && (
            <button className="btn btn-sm btn-primary rounded-pill px-3" onClick={handleAdd}>
                <i className="bi bi-plus-circle me-1"></i>
                Add Student
              </button>
          )}

          {canImportStudents && (
            <button
              className="btn btn-sm btn-outline-secondary rounded-pill px-3"
              onClick={() => setShowImportPanel((open) => !open)}
              aria-expanded={showImportPanel}
              aria-controls="student-import-panel"
              disabled={importing}
            >
              <i className="bi bi-upload me-1"></i>
              {importing ? "Importing..." : "Import Excel"}
              <i className={`bi bi-chevron-${showImportPanel ? "up" : "down"} ms-2`} aria-hidden="true"></i>
            </button>
          )}

          {canExportStudents && (
            <button className="btn btn-sm btn-outline-primary rounded-pill px-3" onClick={handleExport}>
            <i className="bi bi-download me-1"></i>
            Export Records
            </button>
          )}
        </div>
      </div>

      {canImportStudents && showImportPanel && (
        <section id="student-import-panel" className="student-import-panel mb-3" aria-label="Import students from Excel" aria-busy={importing}>
          <h3 className="h6 fw-bold">Import students from Excel</h3>
          <div
            className={`student-import-dropzone${importing ? " is-disabled" : ""}`}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              selectImportFile(event.dataTransfer.files);
            }}
          >
            <label htmlFor="student-import-file" className="fw-semibold mb-2">
              Drop your Excel file here or choose a file
            </label>
            <input
              id="student-import-file"
              type="file"
              className="form-control"
              accept=".xlsx,.xls"
              disabled={importing}
              aria-describedby="student-import-help"
              onChange={(event) => {
                selectImportFile(event.target.files);
                event.target.value = "";
              }}
            />
            <small id="student-import-help" className="text-muted d-block mt-2">
              Supports .xlsx and .xls. Students are imported from the first worksheet.
            </small>
          </div>
          {importFile && <p className="small mt-2 mb-0 text-break">Selected file: <strong>{importFile.name}</strong></p>}
          {importError && <div className="alert alert-danger mt-2 mb-0" role="alert">{importError}</div>}
          {importStatus && importStatus !== "error" && (
            <div className="mt-3">
              <div className="d-flex justify-content-between small mb-1" role="status">
                <span>{importStatus === "complete" ? "Import complete" : importStatus === "processing" ? "Upload complete — processing students…" : "Uploading Excel file…"}</span>
                <strong>{importProgress}%</strong>
              </div>
              <div
                className="progress"
                role="progressbar"
                aria-label={importStatus === "complete" ? "Import complete" : "Excel file upload"}
                aria-valuenow={importProgress}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div className={`progress-bar${importing ? " progress-bar-striped progress-bar-animated" : " bg-success"}`} style={{ width: `${importProgress}%` }} />
              </div>
            </div>
          )}
          <button type="button" className="btn btn-primary btn-sm mt-3" disabled={!importFile || importing} onClick={handleImport}>
            {importing ? "Importing students…" : "Start import"}
          </button>
        </section>
      )}

      {/* ✅ Mini stats strip (very less vertical space) */}
      <div className="students-stats-strip mb-2">
        <div className="students-stat mini green">
          <div className="num">{enabledCount}</div>
          <div className="lbl">Active</div>
        </div>
        <div className="students-stat mini red">
          <div className="num">{disabledCount}</div>
          <div className="lbl">Inactive</div>
        </div>
        <div className="students-stat mini blue">
          <div className="num">{totalCount}</div>
          <div className="lbl">Total</div>
        </div>
      </div>

      {/* Filters + table */}
      <div className="card border-0 shadow-sm" style={{ borderRadius: 18, overflow: "hidden" }}>
        <div className="students-status-tabs" role="tablist" aria-label="Student status">
          <button
            type="button"
            role="tab"
            aria-selected={selectedStatus === "enabled"}
            className={`students-status-tab ${selectedStatus === "enabled" ? "active" : ""}`}
            onClick={() => setSelectedStatus("enabled")}
          >
            Active Students <span>{enabledCount}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={selectedStatus === "disabled"}
            className={`students-status-tab ${selectedStatus === "disabled" ? "active" : ""}`}
            onClick={() => setSelectedStatus("disabled")}
          >
            Inactive Students <span>{disabledCount}</span>
          </button>
        </div>

        {/* ✅ compact filter bar + hides extra filters in compact mode */}
        <div className="card-header bg-white border-0 py-3">
          <div className="d-flex flex-wrap gap-2 align-items-center students-filters-row">
            <div className="flex-grow-1">
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ maxWidth: 360, minWidth: 210, borderRadius: 12 }}
                placeholder="Search admission no / name / parent / Aadhaar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <select
              className="form-select form-select-sm"
              style={{ maxWidth: 180 }}
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
            >
              <option value="">All Classes</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.class_name}
                </option>
              ))}
            </select>

            <select
              className="form-select form-select-sm"
              style={{ maxWidth: 160 }}
              value={selectedSection}
              onChange={(e) => setSelectedSection(e.target.value)}
            >
              <option value="">All Sections</option>
              {sections.map((section) => (
                <option key={section.id} value={section.id}>
                  {section.section_name || section.name}
                </option>
              ))}
            </select>

            {!isCompact && (
              <>
                <select
                  className="form-select form-select-sm d-none d-md-block"
                  style={{ maxWidth: 190 }}
                  value={selectedSessionFilter}
                  onChange={(e) => setSelectedSessionFilter(e.target.value)}
                >
                  <option value="">All Sessions</option>
                  {sessions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>

                <select
                  className="form-select form-select-sm d-none d-lg-block"
                  style={{ maxWidth: 160 }}
                  value={hasSiblingFilter}
                  onChange={(e) => setHasSiblingFilter(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="has">With Siblings</option>
                  <option value="no">Without Siblings</option>
                </select>
              </>
            )}

            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => {
                setSearch("");
                setSelectedClass("");
                setSelectedSection("");
                setSelectedStatus("enabled");
                setSelectedSessionFilter("");
                setHasSiblingFilter("");
              }}
              title="Reset filters"
            >
              <i className="bi bi-arrow-clockwise"></i>
            </button>
          </div>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0 align-middle students-table">
              <thead
                className="sticky-top"
                style={{ background: "linear-gradient(90deg,var(--edb-surface) 0%,var(--edb-surface) 100%)" }}
              >
                <tr>
                  <th className="border-0 py-2 d-none d-md-table-cell">#</th>
                  <th className="border-0 py-2">Photo</th>
                  <th className="border-0 py-2 students-sticky-admission">Adm. #</th>

                  {isCompact ? (
                    <th className="border-0 py-2 students-sticky-name students-sticky-info">Info</th>
                  ) : (
                    <>
                      <th className="border-0 py-2 students-sticky-name">Student Name</th>
                      <th className="border-0 py-2 d-none d-lg-table-cell">Father</th>
                      <th className="border-0 py-2">Class</th>
                      <th className="border-0 py-2 d-none d-md-table-cell">Section</th>
                      <th className="border-0 py-2 d-none d-xl-table-cell">House</th>
                      <th className="border-0 py-2 d-none d-lg-table-cell">Session</th>
                      <th className="border-0 py-2 d-none d-xl-table-cell">Aadhaar</th>
                      <th className="border-0 py-2 d-none d-lg-table-cell">Type</th>
                      {canManageStudents && (
                        <th className="border-0 py-2 d-none d-xl-table-cell">Concession</th>
                      )}
                      <th className="border-0 py-2 d-none d-xl-table-cell">Transport</th>
                    </>
                  )}

                  <th className="border-0 py-2">Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredStudents.length ? (
                  filteredStudents
                    .slice()
                    .map((stu, idx) => {
                      const combinedSiblings = [];
                      for (let i = 1; i <= 4; i++) {
                        const sid = stu[`sibling_id_${i}`];
                        const sname = stu[`sibling_name_${i}`];
                        if (sid || sname) {
                          combinedSiblings.push({
                            token: sid ?? null,
                            name: sname ? String(sname) : sid ? siblingDisplayToken(sid) : "Sibling",
                          });
                        }
                      }

                      const compactLine2 = [
                        stu.class_name ? `Class: ${stu.class_name}` : null,
                        stu.section_name ? `Sec: ${stu.section_name}` : null,
                        stu.father_name ? `F: ${stu.father_name}` : null,
                      ]
                        .filter(Boolean)
                        .join(" • ");

                      const admissionTypeLabel = getAdmissionTypeLabel(stu, admissionTypes);
                      const admissionTypeBadgeClass = getAdmissionTypeBadgeClass(admissionTypeLabel);
                      const hasMoreActions =
                        canUploadStudentPhoto ||
                        (canUseTcOperations && !!stu.admission_number) ||
                        !!stu.admission_number ||
                        canDeleteStudents;

                      const compactBadges = (
                        <div className="d-flex flex-wrap gap-1 mt-1">
                          {stu.session_name && (
                            <span className="badge bg-light text-dark border students-badge">
                              {stu.session_name}
                            </span>
                          )}
                          {admissionTypeLabel && admissionTypeLabel !== "-" && (
                            <span className={`badge students-badge ${admissionTypeBadgeClass}`}>
                              {admissionTypeLabel}
                            </span>
                          )}
                          {!!stu.house_name && (
                            <span
                              className="badge students-badge"
                              style={{
                                backgroundColor: stu.house_color || "#6c757d",
                                color: "var(--edb-on-primary)",
                              }}
                            >
                              {stu.house_name}
                            </span>
                          )}
                          {!!stu.route_id && (
                            <span className="badge bg-info text-dark students-badge">
                              {String(formatTransportById(stu.route_id)).slice(0, 18)}
                              {String(formatTransportById(stu.route_id)).length > 18 ? "…" : ""}
                            </span>
                          )}
                        </div>
                      );

                      return (
                        <tr
                          key={stu.id}
                          className="table-hover-row"
                          onClick={() => handleRowClickOpenEdit(stu)} // ✅ CLICK STUDENT -> EDIT POPUP
                          style={{ cursor: canEditStudents ? "pointer" : "default" }}
                          title={canEditStudents ? "Click to edit student" : ""}
                        >
                          <td className="py-2 d-none d-md-table-cell">{idx + 1}</td>

                          <td className="py-1">
                            <PhotoCell student={stu} />
                          </td>

                          <td className="py-2 fw-medium students-sticky-admission" style={{ whiteSpace: "nowrap" }}>
                            {stu.admission_number || "-"}
                          </td>

                          {isCompact ? (
                            <td className="py-2 students-sticky-name students-sticky-info">
                              <div className="fw-semibold students-name-compact">{stu.name || "-"}</div>

                              {combinedSiblings.length > 0 && (
                                <div className="mt-1">
                                  {combinedSiblings.slice(0, 3).map((sibling, i) => (
                                    <span
                                      key={i}
                                      className="badge rounded-pill me-1 mb-1"
                                      style={{
                                        backgroundColor: SIBLING_COLORS[i % SIBLING_COLORS.length].bg,
                                        color: SIBLING_COLORS[i % SIBLING_COLORS.length].text,
                                        fontSize: "0.7rem",
                                        cursor: sibling.token ? "pointer" : "default",
                                        border: "none",
                                      }}
                                      title={sibling.name}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (sibling.token) handleSiblingClick(sibling.token);
                                      }}
                                    >
                                      {sibling.name.length > 14
                                        ? `${sibling.name.substring(0, 11)}...`
                                        : sibling.name}
                                    </span>
                                  ))}
                                  {combinedSiblings.length > 3 && (
                                    <span
                                      className="badge bg-secondary rounded-pill mb-1"
                                      style={{ fontSize: "0.7rem" }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      +{combinedSiblings.length - 3}
                                    </span>
                                  )}
                                </div>
                              )}

                              {compactLine2 && <div className="text-muted students-compact-sub">{compactLine2}</div>}
                              {compactBadges}
                            </td>
                          ) : (
                            <>
                              <td className="py-2 students-sticky-name">
                                <div className="fw-semibold">{stu.name}</div>

                                {combinedSiblings.length > 0 && (
                                  <div className="mt-1">
                                    {combinedSiblings.map((sibling, i) => (
                                      <span
                                        key={i}
                                        className="badge rounded-pill me-1 mb-1"
                                        style={{
                                          backgroundColor: SIBLING_COLORS[i % SIBLING_COLORS.length].bg,
                                          color: SIBLING_COLORS[i % SIBLING_COLORS.length].text,
                                          fontSize: "0.7rem",
                                          cursor: sibling.token ? "pointer" : "default",
                                          border: "none",
                                        }}
                                        title={sibling.name}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (sibling.token) handleSiblingClick(sibling.token);
                                        }}
                                      >
                                        {sibling.name.length > 15
                                          ? `${sibling.name.substring(0, 12)}...`
                                          : sibling.name}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </td>

                              <td className="py-2 text-muted d-none d-lg-table-cell">{stu.father_name || "-"}</td>

                              <td className="py-2">
                                <span className="badge bg-light text-dark border students-badge">
                                  {stu.class_name || "-"}
                                </span>
                              </td>

                              <td className="py-2 d-none d-md-table-cell">
                                <span className="badge bg-secondary students-badge">{stu.section_name || "-"}</span>
                              </td>

                              <td className="py-2 d-none d-xl-table-cell">
                                {stu.house_name ? (
                                  <span
                                    className="badge students-badge"
                                    style={{ backgroundColor: stu.house_color || "#6c757d", color: "var(--edb-on-primary)" }}
                                  >
                                    {stu.house_name}
                                  </span>
                                ) : (
                                  "-"
                                )}
                              </td>

                              <td className="py-2 d-none d-lg-table-cell">
                                {stu.session_name || (stu.session_id ? "Assigned" : "-")}
                              </td>

                              <td className="py-2 small d-none d-xl-table-cell">
                                {stu.aadhaar_number
                                  ? stu.aadhaar_number.replace(/(\d{4})(\d{4})(\d{4})/, "$1-$2-$3")
                                  : "-"}
                              </td>

                              <td className="py-2 d-none d-lg-table-cell">
                                <span className={`badge students-badge ${admissionTypeBadgeClass}`}>
                                  {admissionTypeLabel || "-"}
                                </span>
                              </td>

                              {canManageStudents && (
                                <td className="py-2 d-none d-xl-table-cell">
                                  <span className="badge bg-info text-dark students-badge">
                                    {stu.concession_name || "-"}
                                  </span>
                                </td>
                              )}

                              <td className="py-2 small d-none d-xl-table-cell">
                                {formatTransportById(stu.route_id)}
                              </td>
                            </>
                          )}

                          <td className="py-1">
                            <div className="students-actions">
                              {canToggleStudentStatus && (
                                <div className="form-check form-switch form-switch-sm m-0">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`status-${stu.id}`}
                                      checked={stu.status === "enabled"}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        toggleStudentStatus(stu);
                                      }}
                                    />
                                  </div>
                              )}

                              {canEditStudents && (
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEdit(stu);
                                    }}
                                    title="Edit"
                                  >
                                    <i className="bi bi-pencil"></i>
                                </button>
                              )}

                              {stu.admission_number && (
                                <button
                                  className="btn btn-outline-dark btn-sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewAttendance(stu);
                                  }}
                                  title="View Attendance"
                                >
                                  <i className="bi bi-calendar-check"></i>
                                </button>
                              )}

                              {canPrintAdmissionForm && (
                              <button
                                className="btn btn-outline-success btn-sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePrintAdmissionForm(stu);
                                }}
                                title="Print Admission Form"
                              >
                                <i className="bi bi-printer"></i>
                              </button>
                              )}

                              {hasMoreActions && (
                                <div className="students-more-wrap">
                                  <button
                                    className="btn btn-outline-secondary btn-sm"
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenActionMenuId((current) => (current === stu.id ? null : stu.id));
                                    }}
                                    title="More actions"
                                  >
                                    <i className="bi bi-three-dots-vertical"></i>
                                  </button>

                                  {openActionMenuId === stu.id && (
                                    <div className="students-more-menu" onClick={(e) => e.stopPropagation()}>
                                      {(isAdmin || isSuperadmin || isPrincipal) && (
                                        <button
                                          type="button"
                                          className="students-more-item"
                                          onClick={() => {
                                            setOpenActionMenuId(null);
                                            navigate(`/student-360/${stu.id}`);
                                          }}
                                        >
                                          <i className="bi bi-box-arrow-up-right"></i>
                                          Open 360° Student View
                                        </button>
                                      )}
                                      {canUseTcOperations && stu.admission_number && (
                                        <button
                                          type="button"
                                          className="students-more-item"
                                          onClick={() => {
                                            setOpenActionMenuId(null);
                                            handleTcOperations(stu);
                                          }}
                                        >
                                          <i className="bi bi-file-earmark-text"></i>
                                          TC Operations
                                        </button>
                                      )}

                                      {canUploadStudentPhoto && (
                                        <button
                                          type="button"
                                          className="students-more-item"
                                          onClick={() => {
                                            setOpenActionMenuId(null);
                                            promptAndUploadPhoto(stu);
                                          }}
                                        >
                                          <i className="bi bi-camera"></i>
                                          {stu.photo ? "Replace Photo" : "Upload Photo"}
                                        </button>
                                      )}

                                      {stu.admission_number && (
                                        <button
                                          type="button"
                                          className="students-more-item"
                                          onClick={() => {
                                            setOpenActionMenuId(null);
                                            window.open(buildDirectPaymentPath(stu), "_blank", "noopener,noreferrer");
                                          }}
                                        >
                                          <i className="bi bi-credit-card"></i>
                                          Direct Payment
                                        </button>
                                      )}

                                      {canDeleteStudents && (
                                        <button
                                          type="button"
                                          className="students-more-item danger"
                                          onClick={() => {
                                            setOpenActionMenuId(null);
                                            handleDelete(stu.id, stu.name);
                                          }}
                                        >
                                          <i className="bi bi-trash"></i>
                                          Delete
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                ) : (
                  <tr>
                    <td colSpan={isCompact ? 5 : canManageStudents ? 13 : 12} className="text-center py-4">
                      <div className="text-muted">
                        <i className="bi bi-inbox display-6 mb-2"></i>
                        <p className="mb-0">No students match the current filters.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Students;
